import axios from 'axios';

// Use production API URL - backend handles authentication
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bdm.purelocations.com.au/api/sf';

const BATCH_SIZE = 199;
const QUERY_DELAY = 200;

// Enhanced logging
const log = (type, message, data = null) => {
  // Remove the unused timestamp variable
  // const timestamp = new Date().toISOString();
  // console.log(`[${timestamp}] [SF-${type}] ${message}`);
  if (data) {
    // console.log(JSON.stringify(data, null, 2));
  }
};

// Error logging
const logError = (type, error, context = {}) => {
  const timestamp = new Date().toISOString();
  console.error(`[${timestamp}] [SF-ERROR] [${type}]`, {
    message: error.message,
    response: error.response?.data,
    status: error.response?.status,
    context,
    stack: error.stack
  });
};

class SalesforceService {
  constructor() {
    this.accessToken = null;
    this.instanceUrl = null;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.retryDelay = 1000;
  }

  /**
   * Generic SOQL query helper
   */
  async query(soql) {
    try {
      if (!soql || typeof soql !== 'string') {
        throw new Error('SOQL query string is required');
      }
      const response = await axios.post(`${API_BASE_URL}/query`, { query: soql });
      return response.data;
    } catch (error) {
      logError('QUERY', error, { context: 'generic query', preview: soql?.slice(0, 120) });
      throw error;
    }
  }

  /**
   * Invoke a Salesforce Flow (custom action) by API name with inputs array
   * @param {string} flowApiName
   * @param {Array<object>} inputs - array of input objects matching flow variables
   */
  async invokeFlow(flowApiName, inputs) {
    try {
      if (!flowApiName) throw new Error('flowApiName is required');
      if (!Array.isArray(inputs) || inputs.length === 0) throw new Error('inputs array is required');

      // Ensure authentication since some consumers call directly
      if (!this.isAuthenticated()) {
        await this.authenticate();
      }

      const urlBase = process.env.NODE_ENV === 'production'
        ? 'https://bdm.purelocations.com.au/api/sf'
        : 'https://bdm.purelocations.com.au/api/sf';

      const response = await axios.post(`${urlBase}/flow/${encodeURIComponent(flowApiName)}`, { inputs }, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      logError('FLOW', error, { context: 'invokeFlow', flowApiName, inputCount: inputs?.length });
      throw error;
    }
  }

  /**
   * Calculate current and previous financial year ranges dynamically
   * Financial year runs from July 1st to June 30th
   */
  getFinancialYearRanges() {
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-based (0 = January, 6 = July)
    const currentYear = now.getFullYear();
    
    let currentFYStartYear, currentFYEndYear;
    
    // If we're in July-December (months 6-11), FY started in current year
    // If we're in January-June (months 0-5), FY started in previous year
    if (currentMonth >= 6) { // July-December
      currentFYStartYear = currentYear;
      currentFYEndYear = currentYear + 1;
    } else { // January-June
      currentFYStartYear = currentYear - 1;
      currentFYEndYear = currentYear;
    }
    
    // Previous financial year is one year before current FY
    const previousFYStartYear = currentFYStartYear - 1;
    const previousFYEndYear = currentFYEndYear - 1;
    
    return {
      current: {
        startYear: currentFYStartYear,
        endYear: currentFYEndYear,
        startDate: `${currentFYStartYear}-07-01T00:00:00Z`,
        endDate: `${currentFYEndYear}-07-01T00:00:00Z`,
        label: `FY${currentFYStartYear}-${currentFYEndYear.toString().slice(-2)}`
      },
      previous: {
        startYear: previousFYStartYear,
        endYear: previousFYEndYear,
        startDate: `${previousFYStartYear}-07-01T00:00:00Z`,
        endDate: `${previousFYEndYear}-07-01T00:00:00Z`,
        label: `FY${previousFYStartYear}-${previousFYEndYear.toString().slice(-2)}`
      }
    };
  }

  async sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async authenticate() {
    try {
      log('AUTH', 'Initiating authentication', { apiBaseUrl: API_BASE_URL });
      const response = await axios.post(`${API_BASE_URL}/auth`, {}, {
        timeout: 30000, // 30 second timeout
        validateStatus: function (status) {
          return status >= 200 && status < 500;
        }
      });

      if (response.status >= 400) {
        const errorMsg = response.data?.error || response.data?.details || `HTTP ${response.status}`;
        logError('AUTH', new Error(errorMsg), {
          status: response.status,
          responseData: response.data
        });
        throw new Error(`Authentication failed: ${errorMsg}`);
      }

      log('AUTH', 'Authentication successful', {
        hasAccessToken: !!response.data.access_token,
        instanceUrl: response.data.instance_url
      });

      this.accessToken = response.data.access_token;
      this.instanceUrl = response.data.instance_url;
      return response.data;
    } catch (error) {
      logError('AUTH', error, {
        errorMessage: error.message,
        errorResponse: error.response?.data,
        errorStatus: error.response?.status,
        errorCode: error.code
      });
      
      // Provide more specific error messages
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('Connection timeout - the server may be down or unreachable. Please check if the server is running.');
      } else if (error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED') {
        throw new Error('Cannot connect to server. Please check if the server is running at ' + API_BASE_URL);
      } else if (error.response?.status === 401) {
        throw new Error('Authentication failed - invalid Salesforce credentials. Please check server configuration.');
      } else if (error.response?.data?.error) {
        throw new Error(`Authentication failed: ${error.response.data.error}`);
      }
      
      throw new Error(`Failed to authenticate with Salesforce: ${error.message || 'Unknown error'}`);
    }
  }

  async getAllAccountIds() {
    try {
      log('QUERY', 'Starting to fetch all account IDs');
      let allIds = [];
      let done = false;
      let nextRecordsUrl = null;
      let batchNumber = 0;

      const initialQuery = `
        SELECT 
          Id,
          Name,
          RecordTypeId
        FROM Account 
        WHERE RecordTypeId IN ('0125g0000004aLaAAI', '0125g0000004aLfAAI')
        ORDER BY Name ASC NULLS LAST
      `;

      while (!done) {
        batchNumber++;
        log('QUERY', `Fetching account IDs batch #${batchNumber}`);

        try {
          const response = await axios.post(`${API_BASE_URL}/query`, {
            query: batchNumber === 1 ? initialQuery : null,
            nextRecordsUrl
          });

          const records = response.data.records;
          allIds = allIds.concat(records.map(record => record.Id));
          
          done = !response.data.nextRecordsUrl;
          nextRecordsUrl = response.data.nextRecordsUrl;

          log('QUERY', `Batch #${batchNumber} complete`, {
            batchSize: records.length,
            totalIdsSoFar: allIds.length,
            hasMore: !done,
            totalSize: response.data.totalSize,
           
          });

          if (!done) {
            await this.sleep(QUERY_DELAY);
          }
        } catch (error) {
          logError('QUERY', error, {
            context: 'getAllAccountIds',
            batchNumber,
            totalIdsSoFar: allIds.length
          });

          if (allIds.length > 0) {
            log('QUERY', `Returning partial results due to error`, {
              totalIds: allIds.length
            });
            return allIds;
          }
          throw error;
        }
      }

      log('QUERY', 'Completed fetching all account IDs', {
        totalIds: allIds.length
      });

      return allIds;
    } catch (error) {
      logError('QUERY', error, { context: 'getAllAccountIds' });
      throw error;
    }
  }

  async getAccountsByIds(ids) {
    try {
      log('QUERY', 'Fetching accounts by IDs', {
        idCount: ids.length,
        sampleIds: ids.slice(0, 5)
      });

      const query = `
        SELECT 
          Id,
          Name,
          RecordTypeId,
          Industry_Sectors__c,
          BillingState,
          BillingStateCode,
          Address__c,
          BillingLatitude,
          BillingLongitude,
          Client_Status__c,
          Phone,
          Website,
          Location_Status__c,
          OwnerId,
          Owner.Name,
          Owner.MediumPhotoUrl,
          Owner.Email,
          AnnualRevenue,
          Client_Bond__c,
          Design__c,
          Client_Cleaning_Fee__c,
          Location_Tier__c,
          IsDeleted,
          SystemModstamp,
          LastModifiedDate,
          (
            SELECT 
              Id,
              RecordTypeId,
              Total_Hire_Fees__c,
              CreatedDate,
              CloseDate,
              Name,
              Brand__c,
              Amount,
              Net_Profit__c,
              Gross_Profit__c,
              Revenue__c,
              Type_of_H__c,
              GST__c,
              Total_Hire_Fees_GST__c,
              StageName,
              Type,
              Location_Name__r.Name,
              Location_Name__r.Address__c,
              Location_Name__r.Design__c,
              Location_Name__r.Location_Tier__c,
              Location_Name__r.Website_Feature_Image_URL__c,
              Location_Name__r.BillingCountry,
              Location_Name__r.BillingState,
              Location_Name__r.Property_Category__c
            FROM Opportunities
            ORDER BY CreatedDate DESC, CloseDate DESC
          ),
          (
            SELECT 
              Id,
              Name,
              Opportunity__r.Name,
              Opportunity__r.RecordTypeId,
              Opportunity__r.Location_Name__c,
              Opportunity__r.Location_Name__r.Name,
              Opportunity__r.Location_Name__r.Website_Feature_Image_URL__c,
              Opportunity__r.Total_Hire_Fees__c,
              Opportunity__r.Start_Date_Time__c,
              Opportunity__r.Date_From__c,
              Opportunity__r.CloseDate,
              Opportunity__r.Type_of_H__c,
              CreatedDate,
              Recce_Stage__c,
              Recce_Start_Date_Time__c
            FROM Potential_Locations__r
            ORDER BY CreatedDate DESC
          )
        FROM Account 
        WHERE Id IN ('${ids.join("','")}')
        ORDER BY Name ASC
      `;

      const response = await axios.post(`${API_BASE_URL}/query`, { query });
      
      // Add debugging for important location
      const importantLocation = response.data.records.find(record => record.Id === '0015g000003WiY4AAK');
      if (ids.includes('0015g000003WiY4AAK')) {
        log('QUERY', 'Important Location Check', {
          locationId: '0015g000003WiY4AAK',
          found: !!importantLocation,
          isDeleted: importantLocation?.IsDeleted,
          lastModified: importantLocation?.LastModifiedDate,
          recordType: importantLocation?.RecordTypeId,
          status: importantLocation?.Location_Status__c
        });
      }

      // Separate and format accounts by RecordTypeId
      const clients = response.data.records
        .filter(record => record.RecordTypeId === '0125g0000004aLaAAI')
        .map(record => ({
          accountId: record.Id,
          clientName: record.Name,
          industry: record.Industry_Sectors__c || 'N/A',
          billingState:
            (record.BillingState && String(record.BillingState).trim()) ||
            record.BillingStateCode ||
            'N/A',
          location: record.Address__c || 'N/A',
          owner: {
            name: record.Owner?.Name || 'N/A',
            photoUrl: record.Owner?.MediumPhotoUrl || '',
            email: record.Owner?.Email || 'N/A'
          },
          opportunities: (record.Opportunities?.records || []).map(opp => {
            // Debug: Log Location_Name__r structure for first opportunity
            if (record.Opportunities?.records?.indexOf(opp) === 0) {
              log('QUERY', 'Sample opportunity Location_Name__r structure', {
                hasLocation: !!opp.Location_Name__r,
                locationKeys: opp.Location_Name__r ? Object.keys(opp.Location_Name__r) : [],
                billingCountry: opp.Location_Name__r?.BillingCountry,
                billingState: opp.Location_Name__r?.BillingState,
                propertyCategory: opp.Location_Name__r?.Property_Category__c,
                locationData: opp.Location_Name__r
              });
            }
            return {
              ...opp,
              typeOfHire: opp.Type_of_H__c || 'N/A'
            };
          }),
          annualRevenue: record.AnnualRevenue || 0,
          phone: record.Phone || 'N/A',
          website: record.Website || 'N/A',
          lastSyncedAt: new Date().toISOString()
        }));

      const locations = response.data.records
        .filter(record => record.RecordTypeId === '0125g0000004aLfAAI')
        .map(record => ({
          accountId: record.Id,
          name: record.Name,
          status: record.Location_Status__c,
          address: record.Address__c || 'N/A',
          design: record.Design__c || 'N/A',
          locationTier: record.Location_Tier__c || 'N/A',
          latitude: record.BillingLatitude || null,
          longitude: record.BillingLongitude || null,
          lastSyncedAt: new Date().toISOString(),
          potentialLocations: (record.Potential_Locations__r?.records || []).map(pl => ({
            ...pl,
            typeOfHire: pl.Opportunity__r?.Type_of_H__c || 'N/A'
          }))
        }));
      
      log('QUERY', 'Successfully fetched accounts', {
        requestedIds: ids.length,
        receivedRecords: response.data.records.length,
        clientsCount: clients.length,
        locationsCount: locations.length,
        missingRecords: ids.length - response.data.records.length
      });

      return { clients, locations };
    } catch (error) {
      logError('QUERY', error, {
        context: 'getAccountsByIds',
        idCount: ids.length
      });
      throw error;
    }
  }

  async batchQuery() {
    try {
      this.retryCount = 0;
      log('BATCH', 'Starting batch query process');

      // First get all account IDs
      log('BATCH', 'Fetching all account IDs...');
      const allIds = await this.getAllAccountIds();
      log('BATCH', `Total accounts found: ${allIds.length}`);

      // Split IDs into chunks
      const chunks = [];
      for (let i = 0; i < allIds.length; i += BATCH_SIZE) {
        chunks.push(allIds.slice(i, i + BATCH_SIZE));
      }
      log('BATCH', `Split into ${chunks.length} chunks of ${BATCH_SIZE}`);

      // Process each chunk
      let allClients = [];
      let allLocations = [];
      
      for (let i = 0; i < chunks.length; i++) {
        log('BATCH', `Processing chunk ${i + 1} of ${chunks.length}`);
        
        try {
          const { clients, locations } = await this.getAccountsByIds(chunks[i]);
          allClients = allClients.concat(clients);
          allLocations = allLocations.concat(locations);
          log('BATCH', `Progress: ${allClients.length + allLocations.length}/${allIds.length} accounts processed`);

          // Add a delay between chunks to prevent rate limiting
          if (i < chunks.length - 1) {
            await this.sleep(QUERY_DELAY);
          }
        } catch (error) {
          logError('BATCH', error, {
            context: 'Processing chunk',
            chunkIndex: i,
            chunkSize: chunks[i].length
          });
          
          // Retry logic for chunk processing
          if (this.retryCount < this.maxRetries) {
            this.retryCount++;
            log('BATCH', `Retrying chunk ${i + 1} (Attempt ${this.retryCount} of ${this.maxRetries})`);
            i--; // Retry the same chunk
            await this.sleep(this.retryDelay * this.retryCount);
            continue;
          }

          // If we've already got some records, return those rather than failing completely
          if (allClients.length > 0 || allLocations.length > 0) {
            log('BATCH', `Returning partial results due to error`, {
              processedClients: allClients.length,
              processedLocations: allLocations.length
            });
            return { clients: allClients, locations: allLocations };
          }
          throw error;
        }
      }

      log('BATCH', 'Completed processing all accounts', {
        totalClients: allClients.length,
        totalLocations: allLocations.length
      });

      return { clients: allClients, locations: allLocations };
    } catch (error) {
      logError('BATCH', error, { context: 'batchQuery' });
      throw error;
    }
  }

  async getFinancialYearOpportunities() {
    try {
      log('QUERY', 'Fetching financial year opportunities by month');

      const fyRanges = this.getFinancialYearRanges();
      const currentFY = fyRanges.current;

      const query = `
        SELECT 
          CALENDAR_MONTH(Start_Date_Time__c) Month,
          COUNT(Id) OpportunityCount,
          SUM(Total_Hire_Fees__c) TotalRevenue,
          SUM(Gross_Profit__c) TotalProfit,
          CALENDAR_YEAR(Start_Date_Time__c) Year
        FROM Opportunity 
        WHERE Start_Date_Time__c >= ${currentFY.startDate} 
        AND Start_Date_Time__c < ${currentFY.endDate}
        AND (
          StageName IN ('Closed Won', 'Location Chosen', 'Booking Confirmed', 
                       'Draft Contracts', 'Contract in DS', 'Signed Contract', 
                       'Entry Instructions', 'Case')
          OR (StageName = 'Enquiry' AND CALENDAR_MONTH(Start_Date_Time__c) = 1 
              AND CALENDAR_YEAR(Start_Date_Time__c) = ${currentFY.endYear})
        )
        GROUP BY CALENDAR_MONTH(Start_Date_Time__c), CALENDAR_YEAR(Start_Date_Time__c)
        ORDER BY CALENDAR_YEAR(Start_Date_Time__c), CALENDAR_MONTH(Start_Date_Time__c)
      `;

      log('QUERY', 'Executing query', { 
        query, 
        financialYear: currentFY.label,
        startDate: currentFY.startDate,
        endDate: currentFY.endDate
      });
      const response = await axios.post(`${API_BASE_URL}/query`, { query });
      
      // Initialize array with zeros for all 12 months
      const monthlyData = Array(12).fill().map((_, index) => {
        // For July-Dec, use start year. For Jan-June, use end year
        const year = index < 6 ? currentFY.startYear : currentFY.endYear;
        const month = index < 6 ? index + 7 : index - 5;
        return {
          month,
          monthName: new Date(year, month - 1).toLocaleString('default', { month: 'long' }),
          opportunityCount: 0,
          totalRevenue: 0,
          totalProfit: 0,
          year
        };
      });

      // Process the records
      response.data.records.forEach(record => {
        const month = record.Month;
        const year = record.Year;
        
        // Calculate the index in our array (July = 0, June = 11)
        let monthIndex;
        if (year === currentFY.startYear && month >= 7) {
          monthIndex = month - 7;
        } else if (year === currentFY.endYear && month <= 6) {
          monthIndex = month + 5;
        } else {
          return; // Skip if outside our financial year
        }

        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyData[monthIndex] = {
            month,
            monthName: new Date(year, month - 1).toLocaleString('default', { month: 'long' }),
            opportunityCount: record.OpportunityCount || 0,
            totalRevenue: record.TotalRevenue || 0,
            totalProfit: record.TotalProfit || 0,
            year
          };
        }
      });

      log('QUERY', 'Financial year data processed', {
        financialYear: currentFY.label,
        monthsWithData: monthlyData.filter(m => m.opportunityCount > 0).length,
        totalOpportunities: monthlyData.reduce((sum, m) => sum + m.opportunityCount, 0)
      });

      return monthlyData;

    } catch (error) {
      logError('QUERY', error, { 
        context: 'getFinancialYearOpportunities',
        error: error.message,
        response: error.response?.data
      });
      throw error;
    }
  }

  async getPreviousFinancialYearOpportunities() {
    try {
      log('QUERY', 'Fetching previous financial year opportunities by month');

      const fyRanges = this.getFinancialYearRanges();
      const previousFY = fyRanges.previous;

      const query = `
        SELECT 
          CALENDAR_MONTH(Start_Date_Time__c) Month,
          COUNT(Id) OpportunityCount,
          SUM(Total_Hire_Fees__c) TotalRevenue,
          SUM(Gross_Profit__c) TotalProfit,
          CALENDAR_YEAR(Start_Date_Time__c) Year
        FROM Opportunity 
        WHERE Start_Date_Time__c >= ${previousFY.startDate} 
        AND Start_Date_Time__c < ${previousFY.endDate}
        AND (
          StageName IN ('Closed Won', 'Location Chosen', 'Booking Confirmed', 
                       'Draft Contracts', 'Contract in DS', 'Signed Contract', 
                       'Entry Instructions')
          OR (StageName = 'Enquiry' AND CALENDAR_MONTH(Start_Date_Time__c) = 1 
              AND CALENDAR_YEAR(Start_Date_Time__c) = ${previousFY.endYear})
        )
        GROUP BY CALENDAR_MONTH(Start_Date_Time__c), CALENDAR_YEAR(Start_Date_Time__c)
        ORDER BY CALENDAR_YEAR(Start_Date_Time__c), CALENDAR_MONTH(Start_Date_Time__c)
      `;

      log('QUERY', 'Executing previous year query', { 
        query,
        financialYear: previousFY.label,
        startDate: previousFY.startDate,
        endDate: previousFY.endDate
      });
      const response = await axios.post(`${API_BASE_URL}/query`, { query });
      
      // Initialize array with zeros for all 12 months
      const monthlyData = Array(12).fill().map((_, index) => {
        // For July-Dec, use start year. For Jan-June, use end year
        const year = index < 6 ? previousFY.startYear : previousFY.endYear;
        const month = index < 6 ? index + 7 : index - 5;
        return {
          month,
          monthName: new Date(year, month - 1).toLocaleString('default', { month: 'long' }),
          opportunityCount: 0,
          totalRevenue: 0,
          totalProfit: 0,
          year
        };
      });

      // Process the records
      response.data.records.forEach(record => {
        const month = record.Month;
        const year = record.Year;
        
        // Calculate the index in our array (July = 0, June = 11)
        let monthIndex;
        if (year === previousFY.startYear && month >= 7) {
          monthIndex = month - 7;
        } else if (year === previousFY.endYear && month <= 6) {
          monthIndex = month + 5;
        } else {
          return; // Skip if outside our financial year
        }

        if (monthIndex >= 0 && monthIndex < 12) {
          monthlyData[monthIndex] = {
            month,
            monthName: new Date(year, month - 1).toLocaleString('default', { month: 'long' }),
            opportunityCount: record.OpportunityCount || 0,
            totalRevenue: record.TotalRevenue || 0,
            totalProfit: record.TotalProfit || 0,
            year
          };
        }
      });

      log('QUERY', 'Previous financial year data processed', {
        financialYear: previousFY.label,
        monthsWithData: monthlyData.filter(m => m.opportunityCount > 0).length,
        totalOpportunities: monthlyData.reduce((sum, m) => sum + m.opportunityCount, 0)
      });

      return monthlyData;

    } catch (error) {
      logError('QUERY', error, { 
        context: 'getPreviousFinancialYearOpportunities',
        error: error.message,
        response: error.response?.data
      });
      throw error;
    }
  }

  // Add test function
  async testFinancialYearQuery() {
    try {
      log('TEST', 'Starting financial year opportunities test');
      
      if (!this.isAuthenticated()) {
        log('TEST', 'Not authenticated, authenticating first...');
        await this.authenticate();
      }

      const results = await this.getFinancialYearOpportunities();
      
      log('TEST', 'Test completed successfully', {
        monthsWithData: results.filter(m => m.opportunityCount > 0).length,
        totalOpportunities: results.reduce((sum, m) => sum + m.opportunityCount, 0),
        monthlyBreakdown: results.map(m => ({
          month: m.monthName,
          opportunities: m.opportunityCount,
          revenue: m.totalRevenue,
          profit: m.totalProfit
        }))
      });

      return results;
    } catch (error) {
      logError('TEST', error, { context: 'testFinancialYearQuery' });
      throw error;
    }
  }

  async getPreviousFinancialYearQuery() {
    try {
      log('TEST', 'Starting previous financial year opportunities test');
      
      if (!this.isAuthenticated()) {
        log('TEST', 'Not authenticated, authenticating first...');
        await this.authenticate();
      }

      const results = await this.getPreviousFinancialYearOpportunities();
      
      log('TEST', 'Previous year test completed successfully', {
        monthsWithData: results.filter(m => m.opportunityCount > 0).length,
        totalOpportunities: results.reduce((sum, m) => sum + m.opportunityCount, 0),
        monthlyBreakdown: results.map(m => ({
          month: m.monthName,
          opportunities: m.opportunityCount,
          revenue: m.totalRevenue,
          profit: m.totalProfit
        }))
      });

      return results;
    } catch (error) {
      logError('TEST', error, { context: 'getPreviousFinancialYearQuery' });
      throw error;
    }
  }

  async getOpportunitiesComparison() {
    try {
      log('QUERY', 'Fetching opportunities comparison for current and previous financial years');

      const fyRanges = this.getFinancialYearRanges();
      const currentFY = fyRanges.current;
      const previousFY = fyRanges.previous;

      const query = `
        SELECT 
          CALENDAR_MONTH(CloseDate) Month,
          CALENDAR_YEAR(CloseDate) Year,
          StageName,
          COUNT(Id) OpportunityCount
        FROM Opportunity 
        WHERE CloseDate >= ${previousFY.startDate.split('T')[0]} 
        AND CloseDate <= ${currentFY.endDate.split('T')[0]}
        
        GROUP BY CALENDAR_MONTH(CloseDate), CALENDAR_YEAR(CloseDate), StageName
        ORDER BY CALENDAR_YEAR(CloseDate), CALENDAR_MONTH(CloseDate)
      `;

      log('QUERY', 'Executing opportunities comparison query', {
        currentFY: currentFY.label,
        previousFY: previousFY.label,
        dateRange: `${previousFY.startDate.split('T')[0]} to ${currentFY.endDate.split('T')[0]}`
      });
      const response = await axios.post(`${API_BASE_URL}/query`, { query });
      
      // Initialize arrays for both financial years (July-June)
      const currentFYData = Array(12).fill(0);
      const previousFYData = Array(12).fill(0);
      const currentFYLostData = Array(12).fill(0);
      const previousFYLostData = Array(12).fill(0);
      let currentFYTotal = 0;
      let previousFYTotal = 0;
      let currentFYLostTotal = 0;
      let previousFYLostTotal = 0;

      // Process the records
      response.data.records.forEach(record => {
        const month = record.Month;
        const year = record.Year;
        const count = record.OpportunityCount || 0;
        const isLost = record.StageName === 'Closed Lost';
        
        // Calculate the index in our array (July = 0, June = 11)
        let monthIndex;
        if (month >= 7) {
          monthIndex = month - 7;
        } else {
          monthIndex = month + 5;
        }

        // Determine which financial year this belongs to
        if ((year === currentFY.startYear && month >= 7) || (year === currentFY.endYear && month <= 6)) {
          // Current FY
          if (isLost) {
            currentFYLostData[monthIndex] += count;
            currentFYLostTotal += count;
          } else {
            currentFYData[monthIndex] += count;
            currentFYTotal += count;
          }
        } else if ((year === previousFY.startYear && month >= 7) || (year === previousFY.endYear && month <= 6)) {
          // Previous FY
          if (isLost) {
            previousFYLostData[monthIndex] += count;
            previousFYLostTotal += count;
          } else {
            previousFYData[monthIndex] += count;
            previousFYTotal += count;
          }
        }
      });

      log('QUERY', 'Opportunities comparison processed', {
        currentFY: currentFY.label,
        previousFY: previousFY.label,
        currentFYTotal,
        previousFYTotal,
        currentFYLostTotal,
        previousFYLostTotal,
        monthsWithData: response.data.records.length
      });

      return {
        currentYear: currentFYData,
        previousYear: previousFYData,
        currentYearLost: currentFYLostData,
        previousYearLost: previousFYLostData,
        currentYearTotal: currentFYTotal,
        previousYearTotal: previousFYTotal,
        currentFYLostTotal,
        previousFYLostTotal,
        currentFY: currentFY.startYear,
        previousFY: previousFY.startYear
      };

    } catch (error) {
      logError('QUERY', error, { 
        context: 'getOpportunitiesComparison',
        error: error.message,
        response: error.response?.data
      });
      throw error;
    }
  }

  async getPipelineOpportunities() {
    try {
      log('QUERY', 'Fetching pipeline opportunities by stage');

      const query = `
        SELECT 
          StageName,
          COUNT(Id) OpportunityCount
        FROM Opportunity 
        WHERE StageName NOT IN ('Closed Won', 'Closed Lost')
        GROUP BY StageName
      `;

      log('QUERY', 'Executing pipeline query');
      const response = await axios.post(`${API_BASE_URL}/query`, { query });

      // Define the correct stage order
      const stageOrder = [
        'Case',
        'Followed Up',
        'Contacted',
        'File Pull',
        'Quoted',
        'Recce',
        'Held',
        'Location Chosen',
        'Booking Confirmed',
        'Draft Contracts',
        'Contract in DS',
        'Signed Contract',
        'Entry Instructions'
      ];

      // Map stages to their order index
      const stageToIndex = Object.fromEntries(
        stageOrder.map((stage, index) => [stage, index])
      );

      const pipelineData = response.data.records
        .map(record => ({
          id: record.StageName,
          label: record.StageName,
          value: record.OpportunityCount,
          orderIndex: stageToIndex[record.StageName] ?? 999, // Put unknown stages at the end
          color: [
            '#FF0000', // Case (Red)
            '#FFB74D', // Followed Up
            '#FFA726', // Contacted
            '#FF9800', // File Pull
            '#FB8C00', // Quoted
            '#F57C00', // Recce
            '#EF6C00', // Held
            '#E65100', // Location Chosen
            '#D84315', // Booking Confirmed
            '#BF360C', // Draft Contracts
            '#C62828', // Contract in DS
            '#B71C1C', // Signed Contract
            '#880E4F'  // Entry Instructions
          ][stageToIndex[record.StageName] ?? 0]
        }))
        .sort((a, b) => a.orderIndex - b.orderIndex); // Sort by the defined order

      log('QUERY', 'Pipeline data processed', {
        totalStages: pipelineData.length,
        totalOpportunities: pipelineData.reduce((sum, item) => sum + item.value, 0),
        stages: pipelineData.map(item => `${item.label}: ${item.value}`)
      });

      return pipelineData;

    } catch (error) {
      logError('QUERY', error, { 
        context: 'getPipelineOpportunities',
        error: error.message,
        response: error.response?.data
      });
      throw error;
    }
  }

  async updateContactPurePromoteFields(contactId, lastContact, lastLocation) {
    try {
      log('UPDATE', 'Updating contact Pure Promote fields', {
        contactId,
        lastContact,
        lastLocation
      });

      // Format the date for Salesforce
      const formattedDate = new Date(lastContact).toISOString();

      // Send the update request with the correct data structure
      const response = await axios.post(`${API_BASE_URL}/update`, {
        contactId,
        lastContact: formattedDate,
        lastLocation
      });
      
      log('UPDATE', 'Successfully updated contact', {
        contactId,
        success: response.data.success,
        lastContact: formattedDate,
        lastLocation
      });

      return response.data;
    } catch (error) {
      logError('UPDATE', error, {
        context: 'updateContactPurePromoteFields',
        contactId,
        lastContact,
        lastLocation
      });
      throw error;
    }
  }

  /**
   * Lookup a Salesforce User ID by email address
   * @param {string} email - The email address of the Salesforce user
   * @returns {Promise<string|null>} - The Salesforce User ID or null if not found
   */
  async getUserIdByEmail(email) {
    try {
      if (!email) {
        log('QUERY', 'No email provided for getUserIdByEmail');
        return null;
      }

      log('QUERY', 'Looking up Salesforce User ID by email', { email });

      // Ensure we're authenticated with Salesforce
      if (!this.accessToken) {
        await this.authenticate();
      }

      // Format email for SOQL, escape single quotes
      const formattedEmail = email.replace(/'/g, "\\'");

      // Create the SOQL query to find the User record
      const query = `
        SELECT Id, Name, Email
        FROM User
        WHERE Email = '${formattedEmail}'
        LIMIT 1
      `;

      const response = await axios.post(`${API_BASE_URL}/query`, 
        { query },
        { 
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.records && response.data.records.length > 0) {
        const userId = response.data.records[0].Id;
        log('QUERY', 'Successfully found User ID', {
          email,
          userId,
          userName: response.data.records[0].Name
        });
        return userId;
      }

      log('QUERY', 'No User found with the provided email', { email });
      return null;
    } catch (error) {
      logError('QUERY', error, {
        context: 'getUserIdByEmail',
        email
      });
      return null; // Return null instead of throwing to make it easier to handle
    }
  }

  async createPurePromoteRecord(contactId, locationId, sendDatetime, ownerID) {
    try {
      log('CREATE', 'Creating Pure_Promote__c record', {
        contactId,
        locationId,
        sendDatetime,
        ownerID,
        contactIdType: typeof contactId,
      });

      // Validate parameters
      if (!contactId) {
        throw new Error('Contact ID is required');
      }
      
      if (!locationId) {
        throw new Error('Location ID is required');
      }

      // First verify the contact exists in Salesforce
      try {
        const contactQuery = `
          SELECT Id, Name, Email, Contact_Notes__c
          FROM Contact
          WHERE Id = '${contactId}'
          LIMIT 1
        `;
        
        const contactResponse = await axios.post(`${API_BASE_URL}/query`, { query: contactQuery });
        if (!contactResponse.data?.records?.length) {
          throw new Error('Contact not found in Salesforce');
        }
        
        const contactNotes = contactResponse.data.records[0].Contact_Notes__c;
        log('CREATE', 'Retrieved contact for Pure_Promote__c creation', {
          contactId,
          hasNotes: !!contactNotes,
          contactName: contactResponse.data.records[0].Name
        });

        // Format the date for Salesforce
        const formattedDate = new Date(sendDatetime).toISOString();

        // Prepare the request body
        const requestBody = {
          Contact_Sent_To__c: contactId,
          Location_Account_Promoted__c: locationId,
          Send_Datetime__c: formattedDate
        };
        
        // Only include OwnerId if provided
        if (ownerID) {
          requestBody.OwnerId = ownerID;
        }
        
        // Include notes if available
        if (contactNotes) {
          requestBody.Contact_Notes__c = contactNotes;
        }
        
        log('CREATE', 'Request body for Pure_Promote__c creation', requestBody);

        // Track the result of each operation
        let purePromoteResult = null;
        let contactUpdateResult = null;
        let usingFallback = false;

        try {
          // Try the new endpoint first to create Pure_Promote__c object
          log('CREATE', 'Attempting to use new createPurePromote endpoint');
          const response = await axios.post(`${API_BASE_URL}/createPurePromote`, requestBody);
          
          purePromoteResult = response.data;
          
          log('CREATE', 'Successfully created Pure_Promote__c record', {
            contactId,
            locationId,
            recordId: purePromoteResult.id
          });
        } catch (endpointError) {
          // If we get a 404, the endpoint doesn't exist yet
          if (endpointError.response && endpointError.response.status === 404) {
            log('CREATE', 'New endpoint not found (404), will only update contact');
            usingFallback = true;
          } else {
            // For other errors, log and continue to try contact update
            logError('CREATE', endpointError, {
              context: 'Pure_Promote__c creation failed, continuing with contact update',
              status: endpointError.response?.status
            });
          }
        }

        // Now also update the Contact record for backward compatibility
        try {
          log('CREATE', 'Updating Contact Pure Promote fields for backward compatibility');
          
          const updateResponse = await axios.post(`${API_BASE_URL}/update`, {
            contactId,
            lastContact: formattedDate,
            lastLocation: locationId
          });
          
          contactUpdateResult = updateResponse.data;
          
          log('CREATE', 'Successfully updated Contact fields', {
            contactId,
            success: contactUpdateResult.success
          });
        } catch (contactError) {
          logError('CREATE', contactError, {
            context: 'Contact update failed after Pure_Promote__c creation',
            contactId
          });
          
          // Only throw if we also failed to create the Pure_Promote__c record
          if (!purePromoteResult) {
            throw contactError;
          }
        }
        
        // Return a combined result
        return {
          success: purePromoteResult?.success || contactUpdateResult?.success,
          message: purePromoteResult 
            ? 'Pure_Promote__c record created successfully' 
            : 'Contact updated successfully',
          id: purePromoteResult?.id || contactId,
          usingFallback,
          contactNotes,
          purePromoteResult,
          contactUpdateResult
        };
      } catch (contactError) {
        logError('CREATE', contactError, {
          context: 'Failed to verify contact existence',
          contactId
        });
        throw new Error('Contact not found in Salesforce');
      }
    } catch (error) {
      logError('CREATE', error, {
        context: 'createPurePromoteRecord',
        contactId,
        locationId,
        sendDatetime,
        ownerID,
        errorResponse: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      throw error;
    }
  }

  isAuthenticated() {
    return !!this.accessToken && !!this.instanceUrl;
  }

  /**
   * Poll for recent Pure Promote records to check for team activity
   * @param {Date} sinceDate - Only get records since this date (ISO string)
   * @returns {Promise<Array>} Array of recent Pure_Promote__c records
   */
  async pollRecentPurePromoteRecords(sinceDate) {
    try {
      if (!sinceDate) {
        // Default to looking back 7 days if no date specified (changed from 24 hours)
        sinceDate = new Date();
        sinceDate.setDate(sinceDate.getDate() - 7);
      }
      
      // Ensure sinceDate is a string in ISO format
      const formattedDate = typeof sinceDate === 'string' ? sinceDate : sinceDate.toISOString();
      
      log('POLL', 'Polling for recent Pure_Promote__c records', {
        sinceDate
      });

      // Try the new API endpoint first (for Pure_Promote__c records)
      try {
        // Format the date properly for Salesforce
        const dateObj = new Date(formattedDate);
        // Format date as YYYY-MM-DDThh:mm:ssZ (no milliseconds)
        const formattedSalesforceDate = dateObj.toISOString().split('.')[0] + 'Z';
        
        log('POLL', 'Sending query to endpoint with formatted date', {
          original: formattedDate,
          formatted: formattedSalesforceDate,
          humanReadable: new Date(formattedSalesforceDate).toLocaleString()
        });
        
        const response = await axios.post(`${API_BASE_URL}/queryPurePromote`, {
          sinceDate: formattedSalesforceDate
        });
        
        if (response.data && response.data.records) {
          log('POLL', 'Found recent Pure_Promote__c records', {
            count: response.data.records.length,
            firstRecordDate: response.data.records.length > 0 
              ? new Date(response.data.records[0].Send_Datetime__c).toLocaleString() 
              : 'No records found'
          });
          return response.data.records;
        }
        
        return [];
      } catch (endpointError) {
        // If we get a 404, the endpoint doesn't exist yet - fall back to querying contacts
        if (endpointError.response && endpointError.response.status === 404) {
          log('POLL', 'Pure_Promote__c query endpoint not found, falling back to contact query');
          
          // Format the date for SOQL - needs to be in the format: YYYY-MM-DDThh:mm:ssZ
          // Ensure it's properly formatted for the SOQL query - datetime literals should NOT be quoted
          const dateObj = new Date(formattedDate);
          // Format date as YYYY-MM-DDThh:mm:ssZ (no milliseconds)
          const soqlDateFormat = dateObj.toISOString().split('.')[0] + 'Z';
          
          log('POLL', 'Formatted date for SOQL query', { 
            original: formattedDate,
            formatted: soqlDateFormat
          });
          
          // Use contact queries as fallback with properly formatted date
          const fallbackQuery = `
            SELECT Id, Pure_Promote_Last_Contact__c, Pure_Promote_Last_Location__c 
            FROM Contact 
            WHERE Pure_Promote_Last_Contact__c >= ${soqlDateFormat}
            ORDER BY Pure_Promote_Last_Contact__c DESC
            LIMIT 100
          `;
          
          log('POLL', 'Executing fallback query', { query: fallbackQuery });
          
          const fallbackResponse = await axios.post(`${API_BASE_URL}/query`, {
            query: fallbackQuery
          });
          
          if (fallbackResponse.data && fallbackResponse.data.records) {
            log('POLL', 'Found recently updated contacts (fallback)', {
              count: fallbackResponse.data.records.length
            });
            
            // Process the contact records to match Pure_Promote__c structure
            return fallbackResponse.data.records.map(contact => {
              // Try to extract location ID from the location string if possible
              let locationAccountId = null;
              const locationString = contact.Pure_Promote_Last_Location__c || '';
              const locationIdMatch = locationString.match(/([a-zA-Z0-9]{15,18})/);
              if (locationIdMatch && locationIdMatch[1]) {
                locationAccountId = locationIdMatch[1];
              }
              
              return {
                Contact_Sent_To__c: contact.Id,
                Send_Datetime__c: contact.Pure_Promote_Last_Contact__c,
                Location_Account_Promoted__c: locationAccountId,
                Location_Name__c: locationString.replace(/\([a-zA-Z0-9]{15,18}\)/g, '').trim(),
                usingFallback: true
              };
            });
          }
          
          return [];
        }
        
        // If it's not a 404, re-throw the error
        throw endpointError;
      }
    } catch (error) {
      logError('POLL', error, {
        context: 'pollRecentPurePromoteRecords',
        sinceDate
      });
      // Return empty array on error rather than throwing
      return [];
    }
  }

  /**
   * Query for Pure Promote records by specific contact IDs or emails
   * @param {Array<string>} contactIdentifiers - Array of Contact IDs or email addresses
   * @param {Date} [sinceDate] - Optional date filter to only get records since this date
   * @returns {Promise<Array>} Array of Pure_Promote__c records
   */
  async queryPurePromoteByContactIds(contactIdentifiers, sinceDate = null) {
    try {
      if (!contactIdentifiers || !Array.isArray(contactIdentifiers) || contactIdentifiers.length === 0) {
        log('QUERY', 'No contact identifiers provided for queryPurePromoteByContactIds');
        return [];
      }
      
      // First, determine if we're dealing with emails or Salesforce IDs
      const isEmailArray = contactIdentifiers.some(id => id.includes('@'));
      
      let salesforceContactIds = contactIdentifiers;
      
      // If we're dealing with emails, query for Contact records first
      if (isEmailArray) {
        log('QUERY', 'Contact identifiers appear to be email addresses, querying contacts first');
        
        // Format emails for SOQL, escape single quotes
        const formattedEmails = contactIdentifiers.map(email => `'${email.replace(/'/g, "\\'")}'`).join(',');
        
        // Query contacts by email addresses
        const contactQuery = `
          SELECT 
            Id, 
            Email, 
            Contact_Notes__c, 
            VIP_Contact__c,
            OwnerId,
            Owner.Name,
            Account.BillingStateCode,
            Account.Industry_Sectors__c
          FROM Contact
          WHERE Email IN (${formattedEmails})
        `;
        
        log('QUERY', 'Querying contacts by email', {
          emailCount: contactIdentifiers.length,
          sampleEmails: contactIdentifiers.slice(0, 3)
        });
        
        try {
          const contactResponse = await axios.post(`${API_BASE_URL}/query`, { query: contactQuery });
          
          if (contactResponse.data && contactResponse.data.records && contactResponse.data.records.length > 0) {
            // Store contact data including notes
            const contactsWithNotes = new Map();
            
            // Map contact IDs to their data
            contactResponse.data.records.forEach(record => {
              contactsWithNotes.set(record.Id, {
                Email: record.Email,
                Contact_Notes__c: record.Contact_Notes__c || null,
                VIP_Contact__c: record.VIP_Contact__c || false,
                ownerId: record.OwnerId || null,
                ownerName: record.Owner?.Name || null,
                billingStateCode: record.Account?.BillingStateCode || null,
                industrySector: record.Account?.Industry_Sectors__c || null
              });
            });
            
            // Still extract just the IDs for the Pure_Promote__c query
            salesforceContactIds = contactResponse.data.records.map(record => record.Id);
            
            log('QUERY', `Found ${salesforceContactIds.length} Salesforce contacts matching the provided emails`);
            
            if (salesforceContactIds.length === 0) {
              log('QUERY', 'No Salesforce contacts found for the provided emails, returning empty array');
              return [];
            }
            
            // After getting the Pure_Promote__c records, we'll merge in the notes
            // Continue with the rest of the function, but we'll later enrich the records
            var contactNotesMap = contactsWithNotes;
          } else {
            log('QUERY', 'No contacts found for the provided email addresses');
            return [];
          }
        } catch (contactError) {
          logError('QUERY', contactError, {
            context: 'Querying contacts by email',
            emailCount: contactIdentifiers.length
          });
          return [];
        }
      }
      
      log('QUERY', `Querying Pure_Promote__c records for ${salesforceContactIds.length} contacts`);
      
      // Format date condition if provided
      let dateCondition = '';
      if (sinceDate) {
        const dateObj = new Date(sinceDate);
        const formattedDate = dateObj.toISOString().split('.')[0] + 'Z';
        dateCondition = ` AND Send_Datetime__c >= ${formattedDate}`;
      }
      
      // Prepare the contact IDs for the IN clause (wrap each ID in single quotes)
      const formattedContactIds = salesforceContactIds.map(id => `'${id}'`).join(',');
      
      // Create the SOQL query to find Pure_Promote__c records for these contacts
      const query = `
        SELECT 
          Id, 
          Contact_Sent_To__c,
          Contact_Sent_To__r.Contact_Notes__c,
          Contact_Sent_To__r.VIP_Contact__c,
          Contact_Sent_To__r.OwnerId,
          Contact_Sent_To__r.Owner.Name,
          Contact_Sent_To__r.Account.BillingStateCode,
          Contact_Sent_To__r.Account.Industry_Sectors__c,
          Location_Account_Promoted__c,
          Send_Datetime__c,
          CreatedById,
          OwnerId,
          Name,
          CreatedDate
        FROM Pure_Promote__c 
        WHERE Contact_Sent_To__c IN (${formattedContactIds})${dateCondition}
        ORDER BY Send_Datetime__c DESC
      `;
      
      log('QUERY', 'Executing query for Pure_Promote__c by contact IDs', {
        contactCount: salesforceContactIds.length,
        sampleContactIds: salesforceContactIds.slice(0, 3),
        hasSinceDateFilter: !!sinceDate
      });
      
      const response = await axios.post(`${API_BASE_URL}/query`, { query });
      
      if (response.data && response.data.records) {
        const records = response.data.records;
        log('QUERY', `Found ${records.length} Pure_Promote__c records for these contacts`, {
          sampleRecords: records.slice(0, 2).map(r => ({
            id: r.Id,
            contactId: r.Contact_Sent_To__c,
            datetime: r.Send_Datetime__c
          }))
        });
        
        // Transform Salesforce records into the format we use in the app
        const vipContacts = records.map(record => {
          const contactId = record.Contact_Sent_To__c;
          // If we have notes directly from a relationship field, use it
          if (record.Contact_Sent_To__r) {
            return {
              ...record,
              Contact_Notes__c: record.Contact_Sent_To__r.Contact_Notes__c,
              VIP_Contact__c: record.Contact_Sent_To__r.VIP_Contact__c || false,
              ownerId: record.Contact_Sent_To__r.OwnerId || null,
              ownerName: record.Contact_Sent_To__r.Owner?.Name || null,
              billingStateCode: record.Contact_Sent_To__r.Account?.BillingStateCode || null,
              industrySector: record.Contact_Sent_To__r.Account?.Industry_Sectors__c || null
            };
          }
          // Otherwise, check the contact notes map we built earlier
          else if (contactNotesMap && contactNotesMap.has(contactId)) {
            return {
              ...record,
              Contact_Notes__c: contactNotesMap.get(contactId).Contact_Notes__c,
              VIP_Contact__c: contactNotesMap.get(contactId).VIP_Contact__c || false,
              ownerId: contactNotesMap.get(contactId).ownerId || null,
              ownerName: contactNotesMap.get(contactId).ownerName || null,
              billingStateCode: contactNotesMap.get(contactId).billingStateCode || null,
              industrySector: contactNotesMap.get(contactId).industrySector || null
            };
          }
          return record;
        });
        
        return vipContacts;
      }
      
      return [];
    } catch (error) {
      logError('QUERY', error, {
        context: 'queryPurePromoteByContactIds',
        contactCount: contactIdentifiers?.length || 0
      });
      // Return empty array on error rather than throwing
      return [];
    }
  }

  /**
   * Update Contact_Notes__c field for a specific contact
   * @param {string} contactId - The Salesforce Contact ID
   * @param {string} notes - The notes content to save
   * @returns {Promise<Object>} Result of the update operation
   */
  async updateContactNotes(contactId, notes) {
    try {
      log('UPDATE', 'Updating contact notes', {
        contactId,
        notesLength: notes ? notes.length : 0
      });

      if (!contactId) {
        throw new Error('Contact ID is required');
      }

      // Send the update request
      const response = await axios.post(`${API_BASE_URL}/update`, {
        contactId,
        Contact_Notes__c: notes
      });
      
      log('UPDATE', 'Successfully updated contact notes', {
        contactId,
        success: response.data.success
      });

      return response.data;
    } catch (error) {
      logError('UPDATE', error, {
        context: 'updateContactNotes',
        contactId
      });
      throw error;
    }
  }

  /**
   * Update VIP_Contact__c field for a specific contact
   * @param {string} contactId - The Salesforce Contact ID
   * @param {boolean} isVip - Whether the contact is a VIP
   * @returns {Promise<Object>} Result of the update operation
   */
  async updateContactVipStatus(contactId, isVip) {
    try {
      log('UPDATE', 'Updating contact VIP status', {
        contactId,
        isVip
      });

      if (!contactId) {
        throw new Error('Contact ID is required');
      }

      // Send the update request
      const response = await axios.post(`${API_BASE_URL}/update`, {
        contactId,
        VIP_Contact__c: isVip
      });
      
      log('UPDATE', 'Successfully updated contact VIP status', {
        contactId,
        isVip,
        success: response.data.success
      });

      return response.data;
    } catch (error) {
      logError('UPDATE', error, {
        context: 'updateContactVipStatus',
        contactId,
        isVip
      });
      throw error;
    }
  }

  /**
   * Update OwnerId for a contact (change who owns the contact in Salesforce)
   * @param {string} contactId - The Salesforce Contact ID
   * @param {string} ownerId - The Salesforce User ID of the new owner
   * @returns {Promise<Object>} Result of the update operation
   */
  async updateContactOwner(contactId, ownerId) {
    try {
      log('UPDATE', 'Updating contact owner', {
        contactId,
        ownerId
      });

      if (!contactId) {
        throw new Error('Contact ID is required');
      }
      if (!ownerId) {
        throw new Error('Owner ID is required');
      }

      const response = await axios.post(`${API_BASE_URL}/updateContactOwner`, {
        contactId,
        ownerId
      });

      log('UPDATE', 'Successfully updated contact owner', {
        contactId,
        ownerId,
        success: response.data.success
      });

      return response.data;
    } catch (error) {
      logError('UPDATE', error, {
        context: 'updateContactOwner',
        contactId,
        ownerId
      });
      throw error;
    }
  }

  /**
   * Fetch active Salesforce users for owner assignment dropdown
   * @returns {Promise<Array<{Id: string, Name: string}>>} Array of users
   */
  async getActiveUsersForOwner() {
    try {
      const query = `SELECT Id, Name FROM User WHERE IsActive = true ORDER BY Name`;
      const data = await this.query(query);
      return data?.records || [];
    } catch (error) {
      logError('QUERY', error, { context: 'getActiveUsersForOwner' });
      throw error;
    }
  }

  /**
   * Fetch all VIP contacts directly from Salesforce
   * @returns {Promise<Array>} Array of VIP contacts
   */
  async getVIPContacts() {
    try {
      log('QUERY', 'Fetching VIP contacts from Salesforce');

      // Ensure we're authenticated with Salesforce
      if (!this.accessToken) {
        await this.authenticate();
      }

      // Query VIP contacts with their most recent Pure_Promote__c records
      const query = `
        SELECT 
          Id,
          AccountId,
          Account.Name,
          Account.BillingStateCode,
          Account.Industry_Sectors__c,
          Contact_Notes__c,
          CreatedById,
          CreatedDate,
          Email,
          FirstName,
          LastName,
          MobilePhone,
          Name,
          no_promote__c,
          OwnerId,
          Owner.Name,
          Owner.Email,
          Phone,
          Pure_Promote_Last_Contact__c,
          Pure_Promote_Last_Location__c,
          VIP_Contact__c
        FROM Contact 
        WHERE VIP_Contact__c = true 
        ORDER BY Name ASC
      `;

      log('QUERY', 'Executing VIP contacts query');
      
      try {
        const response = await axios.post(`${API_BASE_URL}/query`, { query });
        console.log('VIP contacts query response:', response.data);
        
        if (response.data && response.data.records) {
          const records = response.data.records;
          console.log('Processing VIP contacts:', records.length);
          
          // Transform Salesforce records into the format we use in the app
          const vipContacts = records.map(record => {
            return {
              id: record.Id,
              contactId: record.Id,
              email: record.Email,
              name: record.Name,
              firstName: record.FirstName,
              lastName: record.LastName,
              phone: record.Phone,
              mobilePhone: record.MobilePhone,
              accountId: record.AccountId,
              company: record.Account?.Name || '',
              ownerId: record.OwnerId,
              ownerName: record.Owner?.Name,
              ownerEmail: record.Owner?.Email,
              // Set status based on Pure_Promote_Last_Contact__c field
              status: record.Pure_Promote_Last_Contact__c ? 'contacted' : 'pending',
              // Use the fields directly from the Contact record
              Pure_Promote_Last_Contact__c: record.Pure_Promote_Last_Contact__c || null,
              Pure_Promote_Last_Location__c: record.Pure_Promote_Last_Location__c || null,
              Contact_Notes__c: record.Contact_Notes__c,
              VIP_Contact__c: true,
              no_promote__c: record.no_promote__c,
              billingStateCode: record.Account?.BillingStateCode || null,
              industrySector: record.Account?.Industry_Sectors__c || null,
              // Add recentActivity data if there's a recent Pure_Promote record
              recentActivity: record.Pure_Promote_Last_Contact__c ? {
                sentTime: new Date(record.Pure_Promote_Last_Contact__c),
                locationId: record.Pure_Promote_Last_Location__c,
                isRecent: true
              } : null,
              // Add empty arrays/defaults for our app's internal tracking
              emailsSent: [],
              lastUpdated: new Date().toISOString()
            };
          });
          
          // After getting the basic contact info, fetch their Pure_Promote records
          if (vipContacts.length > 0) {
            const contactIds = vipContacts.map(contact => contact.id);
            const purePromoteQuery = `
              SELECT 
                Id,
                Contact_Sent_To__c,
                Location_Account_Promoted__c,
                Send_Datetime__c
              FROM Pure_Promote__c
              WHERE Contact_Sent_To__c IN ('${contactIds.join("','")}')
              ORDER BY Send_Datetime__c DESC
            `;

            const purePromoteResponse = await axios.post(`${API_BASE_URL}/query`, { query: purePromoteQuery });
            
            if (purePromoteResponse.data && purePromoteResponse.data.records) {
              // Group Pure_Promote records by contact
              const purePromoteByContact = {};
              purePromoteResponse.data.records.forEach(record => {
                if (!purePromoteByContact[record.Contact_Sent_To__c]) {
                  purePromoteByContact[record.Contact_Sent_To__c] = [];
                }
                purePromoteByContact[record.Contact_Sent_To__c].push(record);
              });

              // Update contacts with their Pure_Promote records
              vipContacts.forEach(contact => {
                const contactPromotes = purePromoteByContact[contact.id] || [];
                if (contactPromotes.length > 0) {
                  const latestPromote = contactPromotes[0];
                  contact.Pure_Promote_Last_Contact__c = latestPromote.Send_Datetime__c;
                  contact.Pure_Promote_Last_Location__c = latestPromote.Location_Account_Promoted__c;
                  contact.status = 'contacted';
                  contact.recentActivity = {
                    sentTime: new Date(latestPromote.Send_Datetime__c),
                    locationId: latestPromote.Location_Account_Promoted__c,
                    isRecent: true
                  };
                }
              });
            }
          }
          
          console.log('Processed VIP contacts:', vipContacts.length);
          return vipContacts;
        }
        
        console.log('No VIP contacts found in response');
        return [];
      } catch (queryError) {
        console.error('Error executing VIP contacts query:', queryError);
        throw queryError;
      }
    } catch (error) {
      logError('QUERY', error, {
        context: 'getVIPContacts',
        error: error.message,
        response: error.response?.data
      });
      throw error;
    }
  }

  /**
   * Get user details by Salesforce User ID
   * @param {string} userId - The Salesforce User ID
   * @returns {Promise<Object|null>} - The Salesforce User details or null if not found
   */
  async getUserById(userId) {
    try {
      if (!userId) {
        log('QUERY', 'No user ID provided for getUserById');
        return null;
      }

      log('QUERY', 'Looking up Salesforce User by ID', { userId });

      // Ensure we're authenticated with Salesforce
      if (!this.accessToken) {
        await this.authenticate();
      }

      // Format userId for SOQL, escape single quotes
      const formattedUserId = userId.replace(/'/g, "\\'");

      // Create the SOQL query to find the User record
      const query = `
        SELECT Id, Name, Email, MediumPhotoUrl
        FROM User
        WHERE Id = '${formattedUserId}'
        LIMIT 1
      `;

      const response = await axios.post(`${API_BASE_URL}/query`, 
        { query },
        { 
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.records && response.data.records.length > 0) {
        const userData = response.data.records[0];
        log('QUERY', 'Successfully found User details', {
          userId,
          userName: userData.Name,
          userEmail: userData.Email
        });
        return userData;
      }

      log('QUERY', 'No User found with the provided ID', { userId });
      return null;
    } catch (error) {
      logError('QUERY', error, {
        context: 'getUserById',
        userId
      });
      return null; // Return null instead of throwing to make it easier to handle
    }
  }

  /**
   * Get RecordType ID by Name for a given SObject type
   * @param {string} sObjectType - The SObject type (e.g., 'Lead', 'Account')
   * @param {string} recordTypeName - The RecordType Name/Label
   * @returns {Promise<string|null>} - The RecordType ID or null if not found
   */
  async getRecordTypeId(sObjectType, recordTypeName) {
    try {
      log('QUERY', 'Looking up RecordType ID', { sObjectType, recordTypeName });

      const query = `
        SELECT Id, Name, DeveloperName
        FROM RecordType
        WHERE SObjectType = '${sObjectType}' 
        AND (Name = '${recordTypeName}' OR DeveloperName = '${recordTypeName}')
        LIMIT 1
      `;

      const response = await axios.post(`${API_BASE_URL}/query`, { query });

      if (response.data && response.data.records && response.data.records.length > 0) {
        const recordTypeId = response.data.records[0].Id;
        log('QUERY', 'Found RecordType ID', {
          sObjectType,
          recordTypeName,
          recordTypeId
        });
        return recordTypeId;
      }

      log('QUERY', 'RecordType not found', { sObjectType, recordTypeName });
      return null;
    } catch (error) {
      logError('QUERY', error, {
        context: 'getRecordTypeId',
        sObjectType,
        recordTypeName
      });
      return null;
    }
  }

  /**
   * Query Leads with RecordType "Location Lead"
   * @returns {Promise<Array>} Array of Lead records
   */
  async getLocationLeads() {
    try {
      log('QUERY', 'Fetching Location Leads from Salesforce');

      // Ensure authentication before making queries
      // The backend handles auth automatically, but we ensure it's ready
      try {
        if (!this.isAuthenticated()) {
          log('AUTH', 'Not authenticated, authenticating before query');
          await this.authenticate();
        }
      } catch (authError) {
        logError('AUTH', authError, { context: 'Pre-query authentication' });
        // Continue anyway - backend will handle auth
      }

      // First, get the RecordType ID
      let recordTypeId = null;
      try {
        recordTypeId = await this.getRecordTypeId('Lead', 'Location Lead');
      } catch (recordTypeError) {
        logError('QUERY', recordTypeError, { context: 'getRecordTypeId' });
        // Continue without RecordType filter
      }
      
      if (!recordTypeId) {
        log('QUERY', 'Location Lead RecordType not found, trying without filter');
        // Fallback: try querying without RecordType filter if RecordType lookup fails
      }

      // Build the query with RecordType filter if we have it
      const recordTypeFilter = recordTypeId 
        ? `RecordTypeId = '${recordTypeId}'` 
        : `RecordType.Name = 'Location Lead'`;

      // Filter by specific statuses
      const statusFilter = `Status IN ('New', 'Pending Approval', 'Waiting on Fees/Images', 'Listing Queue')`;

      const initialQuery = `
        SELECT 
          Id,
          Name,
          Company,
          Email,
          Phone,
          MobilePhone,
          Street,
          City,
          State,
          PostalCode,
          Country,
          Status,
          LeadSource,
          Scouting_Image_URL__c,
          CreatedDate,
          LastModifiedDate,
          RecordTypeId,
          OwnerId,
          Owner.Name,
          Description
        FROM Lead 
        WHERE ${recordTypeFilter} AND ${statusFilter}
        ORDER BY CreatedDate DESC
      `;

      log('QUERY', 'Executing Location Leads query', { 
        recordTypeId,
        hasRecordTypeFilter: !!recordTypeId,
        queryPreview: initialQuery.substring(0, 200)
      });

      // Handle pagination to fetch all records
      let allLeads = [];
      let done = false;
      let nextRecordsUrl = null;
      let batchNumber = 0;

      while (!done) {
        batchNumber++;
        log('QUERY', `Fetching Location Leads batch #${batchNumber}`);

        try {
          const response = await axios.post(`${API_BASE_URL}/query`, {
            query: batchNumber === 1 ? initialQuery : null,
            nextRecordsUrl
          }, {
            timeout: 30000, // 30 second timeout
            validateStatus: function (status) {
              return status >= 200 && status < 500; // Don't throw on 4xx errors
            }
          });

          // Check for error response
          if (response.status >= 400) {
            const errorMsg = response.data?.error || response.data?.message || `HTTP ${response.status}`;
            logError('QUERY', new Error(errorMsg), {
              context: 'getLocationLeads',
              batchNumber,
              status: response.status,
              responseData: response.data
            });
            throw new Error(`Failed to fetch leads: ${errorMsg}`);
          }

          if (response.data && response.data.records) {
            const batchLeads = response.data.records.map(lead => ({
              id: lead.Id,
              Name: lead.Name || '',
              Company: lead.Company || '',
              Email: lead.Email || '',
              Phone: lead.Phone || lead.MobilePhone || '',
              Street: lead.Street || '',
              City: lead.City || '',
              State: lead.State || '',
              PostalCode: lead.PostalCode || '',
              Country: lead.Country || '',
              Status: lead.Status || '',
              LeadSource: lead.LeadSource || '',
              Scouting_Image_URL__c: lead.Scouting_Image_URL__c || '',
              CreatedDate: lead.CreatedDate,
              LastModifiedDate: lead.LastModifiedDate,
              RecordTypeId: lead.RecordTypeId,
              OwnerId: lead.OwnerId,
              OwnerName: lead.Owner?.Name || '',
              Description: lead.Description || ''
            }));

            allLeads = allLeads.concat(batchLeads);

            log('QUERY', `Batch #${batchNumber} complete`, {
              batchSize: batchLeads.length,
              totalLeadsSoFar: allLeads.length,
              totalSize: response.data.totalSize,
              hasMore: !!response.data.nextRecordsUrl
            });

            done = !response.data.nextRecordsUrl;
            nextRecordsUrl = response.data.nextRecordsUrl;

            if (!done) {
              await this.sleep(QUERY_DELAY);
            }
          } else {
            log('QUERY', 'No records in response', {
              batchNumber,
              responseData: response.data
            });
            done = true;
          }
        } catch (error) {
          logError('QUERY', error, {
            context: 'getLocationLeads',
            batchNumber,
            totalLeadsSoFar: allLeads.length,
            errorMessage: error.message,
            errorResponse: error.response?.data,
            errorStatus: error.response?.status
          });

          // If we have partial results, return them
          if (allLeads.length > 0) {
            log('QUERY', 'Returning partial results due to error', {
              totalLeads: allLeads.length,
              error: error.message
            });
            return allLeads;
          }
          
          // Re-throw with more context
          const errorMessage = error.response?.data?.error || 
                              error.response?.data?.details || 
                              error.message || 
                              'Unknown error fetching leads';
          throw new Error(`Failed to load location leads: ${errorMessage}`);
        }
      }

      log('QUERY', 'Successfully fetched all Location Leads', {
        totalCount: allLeads.length
      });

      // Get stage durations for all leads
      // Create a map of leadId -> {Status, CreatedDate} for fallback when no history exists
      const leadStatusMap = new Map();
      allLeads.forEach(lead => {
        leadStatusMap.set(lead.id, {
          Status: lead.Status,
          CreatedDate: lead.CreatedDate
        });
      });

      const leadIds = allLeads.map(lead => lead.id);
      const stageDurations = await this.getLeadStageDurations(leadIds, leadStatusMap);

      // Add stage duration information to each lead
      const leadsWithDurations = allLeads.map(lead => {
        const durationInfo = stageDurations.get(lead.id);
        return {
          ...lead,
          daysInStage: durationInfo?.daysInStage || null,
          statusChangedDate: durationInfo?.statusChangedDate || null
        };
      });

      log('QUERY', 'Added stage durations to Location Leads', {
        leadsWithDuration: leadsWithDurations.filter(l => l.daysInStage !== null).length,
        totalLeads: leadsWithDurations.length
      });

      return leadsWithDurations;
    } catch (error) {
      logError('QUERY', error, {
        context: 'getLocationLeads',
        errorMessage: error.message,
        errorStack: error.stack
      });
      throw error;
    }
  }

  /**
   * Get stage duration for leads by querying LeadHistory
   * Calculates how long each lead has been in their current Status
   * @param {Array<string>} leadIds - Array of Lead IDs
   * @param {Map<string, {Status: string, CreatedDate: string}>} leadStatusMap - Optional map of leadId -> {Status, CreatedDate} for fallback
   * @returns {Promise<Map>} Map of LeadId -> { daysInStage, statusChangedDate, currentStatus }
   */
  async getLeadStageDurations(leadIds, leadStatusMap = null) {
    try {
      if (!leadIds || leadIds.length === 0) {
        return new Map();
      }

      log('QUERY', 'Fetching Lead History for stage durations', { leadCount: leadIds.length });

      // Batch the query to avoid URL length limits
      const BATCH_SIZE = 200;
      const stageDurations = new Map();
      
      for (let i = 0; i < leadIds.length; i += BATCH_SIZE) {
        const batch = leadIds.slice(i, i + BATCH_SIZE);
        const batchIds = batch.map(id => `'${id}'`).join(',');
        
        const historyQuery = `
          SELECT 
            Id,
            LeadId,
            Field,
            OldValue,
            NewValue,
            CreatedDate,
            CreatedBy.Name
          FROM LeadHistory
          WHERE LeadId IN (${batchIds})
          AND Field = 'Status'
          ORDER BY LeadId, CreatedDate DESC
        `;

        try {
          const response = await axios.post(`${API_BASE_URL}/query`, { query: historyQuery });
          const historyRecords = response.data?.records || [];

          // Process history records to find most recent status change for each lead
          const leadStatusChanges = new Map();
          
          historyRecords.forEach(record => {
            const leadId = record.LeadId;
            if (!leadStatusChanges.has(leadId)) {
              // Store the most recent status change (first one due to DESC order)
              leadStatusChanges.set(leadId, {
                statusChangedDate: record.CreatedDate,
                currentStatus: record.NewValue,
                oldStatus: record.OldValue
              });
            }
          });

          // Calculate duration for each lead
          const now = new Date();
          batch.forEach(leadId => {
            if (leadStatusChanges.has(leadId)) {
              // Lead has history - use the most recent status change
              const change = leadStatusChanges.get(leadId);
              const statusChangedDate = new Date(change.statusChangedDate);
              const daysInStage = Math.floor((now - statusChangedDate) / (1000 * 60 * 60 * 24));
              
              stageDurations.set(leadId, {
                daysInStage,
                statusChangedDate: change.statusChangedDate,
                currentStatus: change.currentStatus,
                oldStatus: change.oldStatus
              });
            } else if (leadStatusMap && leadStatusMap.has(leadId)) {
              // Lead has no history - use CreatedDate as fallback (status hasn't changed since creation)
              const leadInfo = leadStatusMap.get(leadId);
              const createdDate = new Date(leadInfo.CreatedDate);
              const daysInStage = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
              
              stageDurations.set(leadId, {
                daysInStage,
                statusChangedDate: leadInfo.CreatedDate,
                currentStatus: leadInfo.Status,
                oldStatus: null
              });
            }
          });

          log('QUERY', `Processed Lead History batch ${Math.floor(i / BATCH_SIZE) + 1}`, {
            batchSize: batch.length,
            historyRecordsFound: historyRecords.length,
            leadsWithHistory: leadStatusChanges.size,
            leadsWithDuration: stageDurations.size
          });
        } catch (batchError) {
          logError('QUERY', batchError, { 
            context: 'getLeadStageDurations batch', 
            batchNumber: Math.floor(i / BATCH_SIZE) + 1 
          });
          // Continue with other batches even if one fails
        }
      }

      log('QUERY', 'Lead stage durations retrieved', { 
        totalLeads: leadIds.length,
        leadsWithDuration: stageDurations.size
      });

      return stageDurations;
    } catch (error) {
      logError('QUERY', error, { context: 'getLeadStageDurations' });
      return new Map();
    }
  }

  /**
   * Update a Lead record
   * @param {string} leadId - The Lead ID
   * @param {Object} fields - Object with field names and values to update
   * @returns {Promise<Object>} Result of the update operation
   */
  async updateLead(leadId, fields) {
    try {
      log('UPDATE', 'Updating Lead record', {
        leadId,
        fields: Object.keys(fields)
      });

      if (!leadId) {
        throw new Error('Lead ID is required');
      }

      // Send the update request
      const response = await axios.post(`${API_BASE_URL}/updateLead`, {
        leadId,
        ...fields
      });
      
      log('UPDATE', 'Successfully updated Lead', {
        leadId,
        success: response.data.success,
        updatedFields: Object.keys(fields)
      });

      return response.data;
    } catch (error) {
      logError('UPDATE', error, {
        context: 'updateLead',
        leadId
      });
      throw error;
    }
  }

  /**
   * Get booking statistics for location accounts
   * @param {Array<string>} locationIds - Array of Account IDs
   * @returns {Promise<Object>} Object mapping locationId to booking stats
   */
  async getLocationBookingStats(locationIds) {
    try {
      if (!locationIds || locationIds.length === 0) {
        return {};
      }

      log('QUERY', 'Fetching booking stats for locations', {
        locationCount: locationIds.length
      });

      // Calculate date 12 months ago
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
      const formattedDate = twelveMonthsAgo.toISOString().split('.')[0] + 'Z';

      // Format location IDs for SOQL
      const formattedLocationIds = locationIds.map(id => `'${id}'`).join(',');

      const query = `
        SELECT 
          Location_Name__c,
          COUNT(Id) BookingCount,
          MAX(EndDateTime__c) LastBookingDate
        FROM Opportunity
        WHERE Location_Name__c IN (${formattedLocationIds})
        AND StageName = 'Closed Won'
        AND EndDateTime__c >= ${formattedDate}
        GROUP BY Location_Name__c
      `;

      log('QUERY', 'Executing booking stats query', {
        dateFilter: formattedDate,
        locationCount: locationIds.length
      });

      const response = await axios.post(`${API_BASE_URL}/query`, { query });

      const stats = {};
      if (response.data && response.data.records) {
        response.data.records.forEach(record => {
          stats[record.Location_Name__c] = {
            bookingCount: record.BookingCount || 0,
            lastBookingDate: record.LastBookingDate || null,
          };
        });
      }

      // Initialize stats for locations with no bookings
      locationIds.forEach(id => {
        if (!stats[id]) {
          stats[id] = {
            bookingCount: 0,
            lastBookingDate: null,
          };
        }
      });

      log('QUERY', 'Booking stats retrieved', {
        locationsWithBookings: Object.keys(stats).length,
        totalBookings: Object.values(stats).reduce((sum, s) => sum + s.bookingCount, 0)
      });

      return stats;
    } catch (error) {
      logError('QUERY', error, {
        context: 'getLocationBookingStats',
        locationCount: locationIds?.length || 0
      });
      // Return empty stats on error
      const emptyStats = {};
      locationIds?.forEach(id => {
        emptyStats[id] = { bookingCount: 0, lastBookingDate: null };
      });
      return emptyStats;
    }
  }

  /**
   * Get Owner_Fees_8_Hrs_Photo_Shoot_Online__c and Website_Feature_Image_URL__c for location accounts
   * @param {Array<string>} locationIds - Array of Account IDs
   * @returns {Promise<Object>} Object mapping locationId to fee and imageUrl
   */
  async getLocationFees(locationIds) {
    try {
      if (!locationIds || locationIds.length === 0) {
        return {};
      }

      log('QUERY', 'Fetching location fees and images', {
        locationCount: locationIds.length
      });

      const formattedLocationIds = locationIds.map(id => `'${id}'`).join(',');

      const query = `
        SELECT 
          Id,
          Owner_Fees_8_Hrs_Photo_Shoot_Online__c,
          Website_Feature_Image_URL__c
        FROM Account
        WHERE Id IN (${formattedLocationIds})
      `;

      const response = await axios.post(`${API_BASE_URL}/query`, { query });

      const fees = {};
      if (response.data && response.data.records) {
        response.data.records.forEach(record => {
          fees[record.Id] = {
            ownerFees: record.Owner_Fees_8_Hrs_Photo_Shoot_Online__c || null,
            imageUrl: record.Website_Feature_Image_URL__c || null,
          };
        });
      }

      log('QUERY', 'Location fees and images retrieved', {
        locationsWithData: Object.keys(fees).length
      });

      return fees;
    } catch (error) {
      logError('QUERY', error, {
        context: 'getLocationFees',
        locationCount: locationIds?.length || 0
      });
      return {};
    }
  }

  /**
   * Get locations grouped by Location_Status__c
   * @returns {Promise<Array>} Array of {status, count} objects
   */
  async getLocationsByStage() {
    try {
      log('QUERY', 'Fetching locations by stage');
      
      const query = `
        SELECT Location_Status__c, COUNT(Id) LocationCount
        FROM Account 
        WHERE RecordTypeId = '0125g0000004aLfAAI'
        GROUP BY Location_Status__c
        ORDER BY Location_Status__c
      `;

      const response = await axios.post(`${API_BASE_URL}/query`, { query });
      
      const stages = (response.data?.records || []).map(record => ({
        status: record.Location_Status__c || 'Unknown',
        count: record.LocationCount || 0
      }));

      log('QUERY', 'Locations by stage retrieved', { stageCount: stages.length });
      return stages;
    } catch (error) {
      logError('QUERY', error, { context: 'getLocationsByStage' });
      return [];
    }
  }

  /**
   * Get Lead stages with historical average time in stage for Location Lead record types
   * Only includes: New, Pending Approval, Waiting on Fees/Images, Listing Queue, Approved
   * Calculates historical averages based on all status transitions, not just current state
   * @returns {Promise<Array>} Array of stage objects with count and averageDaysInStage
   */
  async getLeadStagesWithAverageTime() {
    try {
      log('QUERY', 'Fetching Lead stages with historical average time');
      
      // Get RecordType IDs for Location Lead and Location Scout
      const recordTypeQuery = `
        SELECT Id, Name
        FROM RecordType
        WHERE SObjectType = 'Lead'
        AND (Name = 'Location Lead' OR Name = 'Location Scout')
      `;

      const rtResponse = await axios.post(`${API_BASE_URL}/query`, { query: recordTypeQuery });
      const recordTypeIds = (rtResponse.data?.records || []).map(rt => rt.Id);
      
      if (recordTypeIds.length === 0) {
        log('QUERY', 'No Location Lead or Location Scout record types found');
        return [];
      }

      const recordTypeFilter = recordTypeIds.map(id => `'${id}'`).join(',');
      
      // Only include these specific stages (Approved is excluded as leads get converted to Location accounts)
      const targetStages = ['New', 'Pending Approval', 'Waiting on Fees/Images', 'Listing Queue'];
      const stageFilter = targetStages.map(stage => `'${stage}'`).join(',');

      // Query ALL Leads with these record types (not just current status)
      // We need all leads to calculate historical averages
      const leadsQuery = `
        SELECT Id, Status, CreatedDate
        FROM Lead
        WHERE RecordTypeId IN (${recordTypeFilter})
      `;

      const leadsResponse = await axios.post(`${API_BASE_URL}/query`, { query: leadsQuery });
      const allLeads = leadsResponse.data?.records || [];

      if (allLeads.length === 0) {
        log('QUERY', 'No leads found matching criteria');
        return targetStages.map(stage => ({
          status: stage,
          count: 0,
          averageDaysInStage: 0
        }));
      }

      // Get ALL status history for all leads (not just current status)
      const leadIds = allLeads.map(lead => lead.Id);
      const historicalAverages = await this.getHistoricalStageAverages(leadIds, targetStages, allLeads);

      // Get current counts for each stage
      const currentCounts = new Map();
      allLeads.forEach(lead => {
        if (targetStages.includes(lead.Status)) {
          currentCounts.set(lead.Status, (currentCounts.get(lead.Status) || 0) + 1);
        }
      });

      // Combine historical averages with current counts
      const results = targetStages.map(stage => ({
        status: stage,
        count: currentCounts.get(stage) || 0,
        averageDaysInStage: historicalAverages.get(stage) || 0
      }));

      log('QUERY', 'Lead stages with historical average time retrieved', { 
        stages: results.length,
        totalLeads: allLeads.length
      });

      return results;
    } catch (error) {
      logError('QUERY', error, { context: 'getLeadStagesWithAverageTime' });
      return [];
    }
  }

  /**
   * Calculate historical average time spent in each stage based on all status transitions
   * @param {Array<string>} leadIds - Array of Lead IDs
   * @param {Array<string>} targetStages - Array of stage names to calculate averages for
   * @param {Array} allLeads - Array of lead records with Status and CreatedDate
   * @returns {Promise<Map>} Map of Stage -> averageDaysInStage
   */
  async getHistoricalStageAverages(leadIds, targetStages, allLeads) {
    try {
      if (!leadIds || leadIds.length === 0) {
        return new Map();
      }

      log('QUERY', 'Calculating historical stage averages', { leadCount: leadIds.length });

      // Batch the query to avoid URL length limits
      const BATCH_SIZE = 200;
      const stageDurations = new Map(); // Map of stage -> array of durations
      
      targetStages.forEach(stage => {
        stageDurations.set(stage, []);
      });

      const now = new Date();
      const leadHistoryMap = new Map(); // Track all history across batches
      
      for (let i = 0; i < leadIds.length; i += BATCH_SIZE) {
        const batch = leadIds.slice(i, i + BATCH_SIZE);
        const batchIds = batch.map(id => `'${id}'`).join(',');
        
        // Get ALL status history records, ordered by lead and date
        const historyQuery = `
          SELECT 
            Id,
            LeadId,
            Field,
            OldValue,
            NewValue,
            CreatedDate,
            CreatedBy.Name
          FROM LeadHistory
          WHERE LeadId IN (${batchIds})
          AND Field = 'Status'
          ORDER BY LeadId, CreatedDate ASC
        `;

        try {
          const response = await axios.post(`${API_BASE_URL}/query`, { query: historyQuery });
          const historyRecords = response.data?.records || [];

          // Accumulate history records across batches (don't process yet)
          historyRecords.forEach(record => {
            if (!leadHistoryMap.has(record.LeadId)) {
              leadHistoryMap.set(record.LeadId, []);
            }
            leadHistoryMap.get(record.LeadId).push(record);
          });

          log('QUERY', `Collected historical data batch ${Math.floor(i / BATCH_SIZE) + 1}`, {
            batchSize: batch.length,
            historyRecordsFound: historyRecords.length
          });
        } catch (batchError) {
          logError('QUERY', batchError, { 
            context: 'getHistoricalStageAverages batch', 
            batchNumber: Math.floor(i / BATCH_SIZE) + 1 
          });
          // Continue with other batches even if one fails
        }
      }

      // Now process all collected history records
      leadHistoryMap.forEach((historyRecords, leadId) => {
        // Sort by date ascending
        historyRecords.sort((a, b) => new Date(a.CreatedDate) - new Date(b.CreatedDate));

        // Get lead's CreatedDate for initial status calculation
        const leadInfo = allLeads.find(l => l.Id === leadId);
        const leadCreatedDate = leadInfo ? new Date(leadInfo.CreatedDate) : null;

        // For each status transition, calculate duration in the previous stage
        for (let j = 0; j < historyRecords.length; j++) {
          const record = historyRecords[j];
          const oldStatus = record.OldValue;
          const newStatus = record.NewValue;
          const transitionDate = new Date(record.CreatedDate);
          
          // Calculate when the lead entered the old status
          let enteredOldStatusDate = null;
          if (j === 0) {
            // First transition - use CreatedDate as entry date for old status
            if (leadCreatedDate && oldStatus && targetStages.includes(oldStatus)) {
              enteredOldStatusDate = leadCreatedDate;
            }
          } else {
            // Previous transition shows when they entered old status
            enteredOldStatusDate = new Date(historyRecords[j - 1].CreatedDate);
          }

          // If oldStatus is one of our target stages, calculate duration
          if (oldStatus && targetStages.includes(oldStatus) && enteredOldStatusDate) {
            const duration = Math.floor((transitionDate - enteredOldStatusDate) / (1000 * 60 * 60 * 24));
            if (duration >= 0) { // Only count positive durations
              stageDurations.get(oldStatus).push(duration);
            }
          }
        }

        // Handle current status - calculate time from last transition to now
        if (historyRecords.length > 0) {
          const lastRecord = historyRecords[historyRecords.length - 1];
          const currentStatus = lastRecord.NewValue;
          const lastTransitionDate = new Date(lastRecord.CreatedDate);
          
          if (currentStatus && targetStages.includes(currentStatus)) {
            const duration = Math.floor((now - lastTransitionDate) / (1000 * 60 * 60 * 24));
            if (duration >= 0) {
              stageDurations.get(currentStatus).push(duration);
            }
          }
        } else if (leadCreatedDate && leadInfo) {
          // Lead has no history - still in original status
          const originalStatus = leadInfo.Status;
          if (originalStatus && targetStages.includes(originalStatus)) {
            const duration = Math.floor((now - leadCreatedDate) / (1000 * 60 * 60 * 24));
            if (duration >= 0) {
              stageDurations.get(originalStatus).push(duration);
            }
          }
        }
      });

      // For leads with no history at all, calculate duration from CreatedDate to now
      const processedLeads = new Set(leadHistoryMap.keys());
      leadIds.forEach(leadId => {
        if (!processedLeads.has(leadId)) {
          const leadInfo = allLeads.find(l => l.Id === leadId);
          if (leadInfo && targetStages.includes(leadInfo.Status)) {
            const createdDate = new Date(leadInfo.CreatedDate);
            const duration = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
            if (duration >= 0) {
              stageDurations.get(leadInfo.Status).push(duration);
            }
          }
        }
      });

      // Calculate averages for each stage
      const averages = new Map();
      stageDurations.forEach((durations, stage) => {
        if (durations.length > 0) {
          const sum = durations.reduce((acc, days) => acc + days, 0);
          const average = Math.round((sum / durations.length) * 10) / 10;
          averages.set(stage, average);
        } else {
          averages.set(stage, 0);
        }
      });

      log('QUERY', 'Historical stage averages calculated', { 
        averages: Object.fromEntries(averages)
      });

      return averages;
    } catch (error) {
      logError('QUERY', error, { context: 'getHistoricalStageAverages' });
      return new Map();
    }
  }

  /**
   * Get locations with Issues__c field populated (red flags)
   * @returns {Promise<Array>} Array of location objects
   */
  async getRedFlagLocations() {
    try {
      log('QUERY', 'Fetching red flag locations');
      
      const query = `
        SELECT Id, Name, Issues__c, LastModifiedDate, Address__c, SystemModstamp
        FROM Account 
        WHERE RecordTypeId = '0125g0000004aLfAAI'
        AND Issues__c != null
        AND Issues__c != ''
        ORDER BY LastModifiedDate DESC
      `;

      const response = await axios.post(`${API_BASE_URL}/query`, { query });
      
      if (!response.data || !response.data.records) {
        logError('QUERY', new Error('Invalid response structure'), { 
          context: 'getRedFlagLocations',
          responseKeys: Object.keys(response.data || {})
        });
        return [];
      }
      
      // Debug: Log the actual response structure
      if (response.data.records.length > 0) {
        const firstRecord = response.data.records[0];
        console.log('DEBUG getRedFlagLocations - First record from Salesforce:', {
          Id: firstRecord.Id,
          Name: firstRecord.Name,
          LastModifiedDate: firstRecord.LastModifiedDate,
          SystemModstamp: firstRecord.SystemModstamp,
          Issues__c: firstRecord.Issues__c,
          allKeys: Object.keys(firstRecord)
        });
      }
      
      const locations = response.data.records.map(record => {
        // Salesforce API returns LastModifiedDate directly on the record
        const lastModified = record.LastModifiedDate || record.SystemModstamp || null;
        
        return {
          id: record.Id,
          name: record.Name,
          issues: record.Issues__c,
          lastModified: lastModified,
          address: record.Address__c || 'N/A'
        };
      });

      console.log('DEBUG getRedFlagLocations - Mapped locations:', {
        total: locations.length,
        withLastModified: locations.filter(l => l.lastModified).length,
        firstLocation: locations[0]
      });

      log('QUERY', 'Red flag locations retrieved', { 
        count: locations.length,
        sampleLastModified: locations[0]?.lastModified 
      });
      return locations;
    } catch (error) {
      logError('QUERY', error, { context: 'getRedFlagLocations' });
      return [];
    }
  }

  /**
   * Get exclusive locations (isExclusive__c == true)
   * @returns {Promise<Object>} Object with count and locations array
   */
  async getExclusiveLocations() {
    try {
      log('QUERY', 'Fetching exclusive locations');
      
      const query = `
        SELECT Id, Name, isExclusive__c, Address__c
        FROM Account 
        WHERE RecordTypeId = '0125g0000004aLfAAI'
        AND isExclusive__c = true
        ORDER BY Name
      `;

      const response = await axios.post(`${API_BASE_URL}/query`, { query });
      
      const locations = (response.data?.records || []).map(record => ({
        id: record.Id,
        name: record.Name,
        address: record.Address__c || 'N/A'
      }));

      log('QUERY', 'Exclusive locations retrieved', { count: locations.length });
      return {
        count: locations.length,
        locations
      };
    } catch (error) {
      logError('QUERY', error, { context: 'getExclusiveLocations' });
      return { count: 0, locations: [] };
    }
  }

  /**
   * Get potential locations (Leads with RecordType "Location Scout")
   * Includes stage duration information
   * @returns {Promise<Array>} Array of lead objects
   */
  async getPotentialLocations() {
    try {
      log('QUERY', 'Fetching potential locations (Location Scout leads)');
      
      // First, get the RecordTypeId for "Location Scout"
      const recordTypeQuery = `
        SELECT Id, Name, DeveloperName
        FROM RecordType
        WHERE SObjectType = 'Lead'
        AND Name = 'Location Scout'
        LIMIT 1
      `;

      const rtResponse = await axios.post(`${API_BASE_URL}/query`, { query: recordTypeQuery });
      const recordTypeId = rtResponse.data?.records?.[0]?.Id;

      if (!recordTypeId) {
        log('QUERY', 'Location Scout RecordType not found');
        return [];
      }

      const query = `
        SELECT Id, Name, Status, CreatedDate, Owner.Name, Owner.Email, Description, Company, Email, Phone
        FROM Lead
        WHERE RecordTypeId = '${recordTypeId}'
        ORDER BY CreatedDate DESC
      `;

      const response = await axios.post(`${API_BASE_URL}/query`, { query });
      
      const leads = (response.data?.records || []).map(record => ({
        id: record.Id,
        name: record.Name || record.Company || 'Unnamed',
        company: record.Company || 'N/A',
        status: record.Status || 'N/A',
        createdDate: record.CreatedDate,
        owner: record.Owner?.Name || 'N/A',
        ownerEmail: record.Owner?.Email || '',
        description: record.Description || '',
        email: record.Email || '',
        phone: record.Phone || ''
      }));

      // Get stage durations for all leads
      // Create a map of leadId -> {Status, CreatedDate} for fallback when no history exists
      const leadStatusMap = new Map();
      leads.forEach(lead => {
        leadStatusMap.set(lead.id, {
          Status: lead.status,
          CreatedDate: lead.createdDate
        });
      });

      const leadIds = leads.map(lead => lead.id);
      const stageDurations = await this.getLeadStageDurations(leadIds, leadStatusMap);

      // Add stage duration information to each lead
      const leadsWithDurations = leads.map(lead => {
        const durationInfo = stageDurations.get(lead.id);
        return {
          ...lead,
          daysInStage: durationInfo?.daysInStage || null,
          statusChangedDate: durationInfo?.statusChangedDate || null
        };
      });

      log('QUERY', 'Potential locations retrieved with stage durations', { 
        count: leadsWithDurations.length,
        leadsWithDuration: leadsWithDurations.filter(l => l.daysInStage !== null).length
      });
      return leadsWithDurations;
    } catch (error) {
      logError('QUERY', error, { context: 'getPotentialLocations' });
      return [];
    }
  }

  /**
   * Get registration stats grouped by state and month from Lead data
   * @param {Date} startDate - Start date from global date range selector
   * @param {Date} endDate - End date from global date range selector
   * @returns {Promise<Array>} Array of registration stats
   */
  async getRegistrationStatsByState(startDate, endDate) {
    // Format dates for SOQL - CreatedDate is a DateTime field
    // Use ISO format: YYYY-MM-DDTHH:mm:ssZ
    const formatDateTimeForSOQL = (date) => {
      const isoString = date.toISOString();
      // Remove milliseconds and ensure Z timezone
      return isoString.split('.')[0] + 'Z';
    };
    
    // Ensure dates are Date objects
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Set to start of day for start date, end of day for end date
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    
    const startDateStr = formatDateTimeForSOQL(start);
    const endDateStr = formatDateTimeForSOQL(end);
    
    // Query Leads grouped by Lead_State__c and month
    // Lead_State__c field contains Australian state (NSW, VIC, QLD, etc.)
    // DateTime fields in SOQL should NOT be quoted
    // Note: ORDER BY must use full expressions, not aliases
    let query = `
      SELECT 
        Lead_State__c,
        CALENDAR_MONTH(CreatedDate) Month,
        CALENDAR_YEAR(CreatedDate) Year,
        COUNT(Id) RegistrationCount
      FROM Lead
      WHERE CreatedDate >= ${startDateStr}
      AND CreatedDate <= ${endDateStr}
      AND Lead_State__c != null
      GROUP BY Lead_State__c, 
               CALENDAR_MONTH(CreatedDate),
               CALENDAR_YEAR(CreatedDate)
      ORDER BY CALENDAR_YEAR(CreatedDate) DESC, CALENDAR_MONTH(CreatedDate) DESC, Lead_State__c
    `;
    
    try {
      log('QUERY', 'Fetching registration stats by state from Leads', { startDate, endDate });
      
      console.log('DEBUG getRegistrationStatsByState: Date range', {
        startDate: startDateStr,
        endDate: endDateStr,
        startDateObj: start,
        endDateObj: end
      });
      
      console.log('DEBUG getRegistrationStatsByState: Query (Lead_State__c)', query);

      const response = await axios.post(`${API_BASE_URL}/query`, { query });
      
      console.log('DEBUG getRegistrationStatsByState: Raw response', {
        status: response.status,
        hasData: !!response.data,
        recordsCount: response.data?.records?.length || 0,
        firstRecord: response.data?.records?.[0],
        totalSize: response.data?.totalSize,
        done: response.data?.done
      });
      
      if (!response.data || !response.data.records) {
        console.error('DEBUG getRegistrationStatsByState: No records in response', response.data);
        return [];
      }
      
      const stats = (response.data.records || []).map(record => {
        console.log('DEBUG getRegistrationStatsByState: Processing record', record);
        return {
          state: record.Lead_State__c || 'Unknown',
          month: record.Month || 0,
          year: record.Year || 0,
          count: record.RegistrationCount || 0
        };
      });

      console.log('DEBUG getRegistrationStatsByState: Mapped stats', {
        count: stats.length,
        sample: stats[0]
      });

      log('QUERY', 'Registration stats by state retrieved', { 
        recordCount: stats.length,
        dateRange: `${startDateStr} to ${endDateStr}`,
        sample: stats[0]
      });
      return stats;
    } catch (error) {
      const errorDetails = {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        errorData: error.response?.data,
        errorCode: error.response?.data?.errorCode,
        errorFields: error.response?.data?.fields,
        fullError: JSON.stringify(error.response?.data, null, 2)
      };
      
      console.error('DEBUG getRegistrationStatsByState: ERROR DETAILS', errorDetails);
      console.error('DEBUG getRegistrationStatsByState: Full error response', error.response);
      console.error('DEBUG getRegistrationStatsByState: Error data', error.response?.data);
      console.error('DEBUG getRegistrationStatsByState: Error message from Salesforce', error.response?.data?.details?.[0]?.message || error.response?.data?.message);
      console.error('DEBUG getRegistrationStatsByState: Query that failed', query);
      
      // Check the actual error message from Salesforce
      const salesforceError = error.response?.data?.details?.[0] || error.response?.data;
      const errorMessage = salesforceError?.message || salesforceError?.error || JSON.stringify(salesforceError);
      console.error('DEBUG getRegistrationStatsByState: Salesforce error message', errorMessage);
      console.error('DEBUG getRegistrationStatsByState: Full error data', error.response?.data);
      
      // If it's a 400 error, try with State field instead (Lead_State__c might not exist)
      if (error.response?.status === 400) {
        console.log('DEBUG getRegistrationStatsByState: Lead_State__c field error detected, trying State field');
        
        // Fallback query using State field instead of Lead_State__c
        // Note: ORDER BY must use full expressions, not aliases
        const fallbackQuery = `
          SELECT 
            State,
            CALENDAR_MONTH(CreatedDate) Month,
            CALENDAR_YEAR(CreatedDate) Year,
            COUNT(Id) RegistrationCount
          FROM Lead
          WHERE CreatedDate >= ${startDateStr}
          AND CreatedDate <= ${endDateStr}
          AND State != null
          GROUP BY State, 
                   CALENDAR_MONTH(CreatedDate),
                   CALENDAR_YEAR(CreatedDate)
          ORDER BY CALENDAR_YEAR(CreatedDate) DESC, CALENDAR_MONTH(CreatedDate) DESC, State
        `;
        
        try {
          console.log('DEBUG getRegistrationStatsByState: Fallback query (using State)', fallbackQuery);
          const fallbackResponse = await axios.post(`${API_BASE_URL}/query`, { query: fallbackQuery });
          
          console.log('DEBUG getRegistrationStatsByState: Fallback response', {
            recordsCount: fallbackResponse.data?.records?.length || 0,
            firstRecord: fallbackResponse.data?.records?.[0]
          });
          
          const fallbackStats = (fallbackResponse.data?.records || []).map(record => ({
            state: record.State || 'Unknown',
            month: record.Month || 0,
            year: record.Year || 0,
            count: record.RegistrationCount || 0
          }));
          
          console.log('DEBUG getRegistrationStatsByState: Fallback stats', {
            count: fallbackStats.length,
            sample: fallbackStats[0]
          });
          
          return fallbackStats;
        } catch (fallbackError) {
          console.error('DEBUG getRegistrationStatsByState: Fallback also failed', {
            status: fallbackError.response?.status,
            errorData: fallbackError.response?.data,
            errorMessage: fallbackError.response?.data?.details?.[0]?.message || fallbackError.response?.data?.message,
            query: fallbackQuery
          });
        }
      }
      
      logError('QUERY', error, { context: 'getRegistrationStatsByState', errorDetails });
      return [];
    }
  }

  /**
   * Get locations with no enquiries or old activity
   * @returns {Promise<Array>} Array of untouched locations
   */
  async getUntouchedLocations() {
    try {
      log('QUERY', 'Fetching untouched locations');
      
      // Get all location accounts
      // Include SystemModstamp as fallback for LastModifiedDate
      const locationsQuery = `
        SELECT Id, Name, CreatedDate, LastModifiedDate, SystemModstamp, Address__c
        FROM Account 
        WHERE RecordTypeId = '0125g0000004aLfAAI'
        ORDER BY LastModifiedDate ASC
      `;

      const locationsResponse = await axios.post(`${API_BASE_URL}/query`, { query: locationsQuery });
      const allLocations = locationsResponse.data?.records || [];

      // Get locations with opportunities
      const locationIds = allLocations.map(loc => `'${loc.Id}'`).join(',');
      if (!locationIds) {
        return [];
      }

      // Batch the query to avoid 414 URI Too Long error
      // Process in chunks of 200 IDs at a time
      const BATCH_SIZE = 200;
      const locationIdsArray = allLocations.map(loc => loc.Id);
      const locationsWithOpps = new Set();
      
      for (let i = 0; i < locationIdsArray.length; i += BATCH_SIZE) {
        const batch = locationIdsArray.slice(i, i + BATCH_SIZE);
        const batchIds = batch.map(id => `'${id}'`).join(',');
        
        const opportunitiesQuery = `
          SELECT Location_Name__c
          FROM Opportunity
          WHERE Location_Name__c IN (${batchIds})
          GROUP BY Location_Name__c
        `;

        try {
          const oppResponse = await axios.post(`${API_BASE_URL}/query`, { query: opportunitiesQuery });
          (oppResponse.data?.records || []).forEach(r => {
            if (r.Location_Name__c) {
              locationsWithOpps.add(r.Location_Name__c);
            }
          });
        } catch (batchError) {
          logError('QUERY', batchError, { context: 'getUntouchedLocations batch', batchNumber: Math.floor(i / BATCH_SIZE) + 1 });
          // Continue with other batches even if one fails
        }
      }

      // Filter to locations without opportunities (enquiries)
      // If a location has enquiries, it's not considered "untouched"
      const untouched = allLocations
        .filter(loc => {
          // Only include locations that have NO opportunities
          return !locationsWithOpps.has(loc.Id);
        })
        .map(loc => {
          // Use LastModifiedDate or SystemModstamp as fallback (same as Red Flags)
          const lastModified = loc.LastModifiedDate || loc.SystemModstamp || null;
          const lastModifiedDate = lastModified ? new Date(lastModified) : null;
          const daysSinceActivity = lastModifiedDate 
            ? Math.floor((new Date() - lastModifiedDate) / (1000 * 60 * 60 * 24))
            : null;
          
          return {
            id: loc.Id,
            name: loc.Name,
            address: loc.Address__c || 'N/A',
            createdDate: loc.CreatedDate,
            lastModified: lastModified,
            hasEnquiries: locationsWithOpps.has(loc.Id),
            daysSinceActivity: daysSinceActivity
          };
        })
        .sort((a, b) => {
          // Sort by daysSinceActivity, handling null values
          if (a.daysSinceActivity === null && b.daysSinceActivity === null) return 0;
          if (a.daysSinceActivity === null) return 1;
          if (b.daysSinceActivity === null) return -1;
          return b.daysSinceActivity - a.daysSinceActivity;
        });

      log('QUERY', 'Untouched locations retrieved', { count: untouched.length });
      return untouched;
    } catch (error) {
      logError('QUERY', error, { context: 'getUntouchedLocations' });
      return [];
    }
  }

  /**
   * Get popular locations comparison - locations that were popular in the last 3 months vs same period last year
   * @returns {Promise<Array>} Array of location comparison objects
   */
  async getPopularLocationsComparison() {
    try {
      log('QUERY', 'Fetching popular locations comparison (last 3 months vs same period last year)');
      
      const now = new Date();
      
      // Calculate last 3 months date range
      const threeMonthsAgo = new Date(now);
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      threeMonthsAgo.setDate(1); // Start of the month 3 months ago
      
      const thisPeriodStart = threeMonthsAgo;
      const thisPeriodEnd = now;
      
      // Calculate same period last year
      const lastYearStart = new Date(thisPeriodStart);
      lastYearStart.setFullYear(lastYearStart.getFullYear() - 1);
      
      const lastYearEnd = new Date(thisPeriodEnd);
      lastYearEnd.setFullYear(lastYearEnd.getFullYear() - 1);
      
      // Format dates for SOQL - Date fields use YYYY-MM-DD format (no quotes needed)
      const formatDateForSOQL = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const lastYearStartStr = formatDateForSOQL(lastYearStart);
      const lastYearEndStr = formatDateForSOQL(lastYearEnd);
      const thisYearStartStr = formatDateForSOQL(thisPeriodStart);
      const thisYearEndStr = formatDateForSOQL(thisPeriodEnd);
      
      console.log('DEBUG: Date ranges for comparison:', {
        lastYearPeriod: `${lastYearStartStr} to ${lastYearEndStr}`,
        thisYearPeriod: `${thisYearStartStr} to ${thisYearEndStr}`
      });
      
      // Query for last year's bookings (same 3-month period) - need to get location names separately since GROUP BY doesn't support relationships
      const lastYearQuery = `
        SELECT 
          Location_Name__c,
          COUNT(Id) BookingCount,
          MAX(CloseDate) LastBookingDate
        FROM Opportunity
        WHERE StageName = 'Closed Won'
        AND CloseDate >= ${lastYearStartStr}
        AND CloseDate <= ${lastYearEndStr}
        AND Location_Name__c != null
        GROUP BY Location_Name__c
        ORDER BY COUNT(Id) DESC
      `;
      
      // Query for this year's bookings (last 3 months)
      const thisYearQuery = `
        SELECT 
          Location_Name__c,
          COUNT(Id) BookingCount,
          MAX(CloseDate) LastBookingDate
        FROM Opportunity
        WHERE StageName = 'Closed Won'
        AND CloseDate >= ${thisYearStartStr}
        AND CloseDate <= ${thisYearEndStr}
        AND Location_Name__c != null
        GROUP BY Location_Name__c
      `;
      
      // Query for all-time last booking date per location
      const allTimeLastBookingQuery = `
        SELECT 
          Location_Name__c,
          MAX(CloseDate) LastBookingDate
        FROM Opportunity
        WHERE StageName = 'Closed Won'
        AND Location_Name__c != null
        GROUP BY Location_Name__c
      `;
      
      // Execute queries in parallel
      const [lastYearResponse, thisYearResponse, allTimeResponse] = await Promise.all([
        axios.post(`${API_BASE_URL}/query`, { query: lastYearQuery }),
        axios.post(`${API_BASE_URL}/query`, { query: thisYearQuery }),
        axios.post(`${API_BASE_URL}/query`, { query: allTimeLastBookingQuery })
      ]);
      
      // Get location IDs that had bookings last year
      const locationIds = [...new Set((lastYearResponse.data?.records || []).map(r => r.Location_Name__c))];
      
      // Query location names separately since GROUP BY doesn't support relationship fields
      let locationNamesMap = new Map();
      if (locationIds.length > 0) {
        const locationIdsStr = locationIds.map(id => `'${id}'`).join(',');
        const locationNamesQuery = `
          SELECT Id, Name
          FROM Account
          WHERE Id IN (${locationIdsStr})
        `;
        
        const locationNamesResponse = await axios.post(`${API_BASE_URL}/query`, { query: locationNamesQuery });
        (locationNamesResponse.data?.records || []).forEach(record => {
          locationNamesMap.set(record.Id, record.Name);
        });
      }
      
      // Build maps for quick lookup
      const lastYearMap = new Map();
      (lastYearResponse.data?.records || []).forEach(record => {
        lastYearMap.set(record.Location_Name__c, {
          count: record.BookingCount || 0
        });
      });
      
      const thisYearMap = new Map();
      (thisYearResponse.data?.records || []).forEach(record => {
        thisYearMap.set(record.Location_Name__c, {
          count: record.BookingCount || 0
        });
      });
      
      const allTimeLastBookingMap = new Map();
      (allTimeResponse.data?.records || []).forEach(record => {
        if (record.LastBookingDate) {
          allTimeLastBookingMap.set(record.Location_Name__c, new Date(record.LastBookingDate));
        }
      });
      
      // Build comparison array
      const comparisons = [];
      lastYearMap.forEach((data, locationId) => {
        const thisYearData = thisYearMap.get(locationId) || { count: 0 };
        const lastBookingDate = allTimeLastBookingMap.get(locationId);
        const locationName = locationNamesMap.get(locationId) || 'Unknown';
        
        const daysSinceLastBooking = lastBookingDate 
          ? Math.floor((now - lastBookingDate) / (1000 * 60 * 60 * 24))
          : null;
        
        comparisons.push({
          locationId: locationId,
          name: locationName,
          lastYearBookings: data.count,
          thisYearBookings: thisYearData.count,
          lastBookingDate: lastBookingDate ? lastBookingDate.toISOString() : null,
          daysSinceLastBooking: daysSinceLastBooking
        });
      });
      
      // Sort by last year bookings descending
      comparisons.sort((a, b) => b.lastYearBookings - a.lastYearBookings);
      
      log('QUERY', 'Popular locations comparison retrieved', { 
        count: comparisons.length,
        sample: comparisons[0],
        locationNamesFound: locationNamesMap.size
      });
      
      return comparisons.slice(0, 50); // Top 50
    } catch (error) {
      logError('QUERY', error, { context: 'getPopularLocationsComparison' });
      return [];
    }
  }
}

const salesforceService = new SalesforceService();
export default salesforceService; 